// interact.jsx — 互动 tab: text/voice/video room
//
// 5/18 (v3.8.0 24h sprint M1): interact text mode 真接 wss://radio.wanwushop.cn/ws/interact
// (GPU :3146 interact_chat.py · DashScope qwen-flash · /no_think 后缀).
// Per-slug 人设 from /data/lipsync_daemon/personas/<slug>.txt (server-side hot reload).
// 首帧实测 ~370ms, 全文 ~600-900ms. 网络失败 fallback simulateReply.

// 5/28 v10.83: page-level click unmute audio (规避 Chrome/Safari autoplay block)
// 默认 voice mode (v10.81 ship), audio 应该一进就听到. 首次任意点击/触摸全页 unmute.
if (typeof window !== "undefined") {
  let unmutedOnce = false;
  const handler = () => {
    if (unmutedOnce) return;
    unmutedOnce = true;
    try {
      document.querySelectorAll("audio, video").forEach(el => {
        if (el.muted) el.muted = false;
        if (el.paused && el.play) el.play().catch(()=>{});
      });
      console.log("[unmute] page-level click unlocked audio");
    } catch (e) {}
  };
  document.addEventListener("click", handler, { once: true, capture: true });
  document.addEventListener("touchend", handler, { once: true, capture: true });
}

var { useState, useEffect, useRef, useCallback, useMemo } = React;  // 5/25 v10.74: var — multi .jsx 共 scope const 撞

const INTERACT_WS_BASE = "wss://radio.wanwushop.cn/ws/interact";
const LIPSYNC_WS_BASE = "wss://radio.wanwushop.cn/lipsync/realtime";

// 哪些 slug 走 LiveKit 帧级流 (需在 lipsync_daemon AVATAR_METADATA 里 prepped).
// 5/18: 当前只有 xiaocheng (xc_L1) 实装, 其他 slug 仍 static portrait + text chat.
/* v10.16: shop-001..100 真互动 LIVE (LivePortrait drive + MuseTalk prep batch 完成).
   每个 shop-XXX 用真自己 portrait 驱动的 10s motion 生成 + 自己 persona.
   alias 5/19 PoC 阶段已 revert, 替为真各自脸. */
// 5/28 v10.80: 加 he-cong (数字 CEO, SoulX-FlashHead paradigm) + 12 CXO (数字团队, lipsync_daemon PREPPED) — 全走 /lipsync/realtime
// 5/28 v10.84: 加 88 director + frontline (43 male + 45 female, sambert preset gender-matched) — total 202 (14 named + 88 dir/frontline + 100 shop)
const LIPSYNC_LIVEKIT_SLUGS = new Set([
  "xiaocheng",
  "he-cong",
  "lin-zhifan", "sarah-chen", "wang-xueyun", "david-park",
  "su-wanqing", "ahmed-al-rashid", "emily-whitman", "batbayar-tumur",
  "dr-hans-muller", "chen-haoming", "yuki-tanaka", "rajesh-patel",
  // 88 director + frontline (5/28 v10.84)
  "amara-okonkwo", "dewi-santoso", "diego-herrera", "francois-dubois",
  "hamza-malik", "isabella-santos", "kerem-yilmaz", "kim-ji-yeon",
  "kristina-sorokina", "lin-xiaoyu", "marcus-thompson", "mei-ling-tan",
  "nguyen-minh-khoa", "nomvula-dlamini", "olena-kovalenko", "siwaphon-rattana",
  "sophia-chen", "yosef-ben-david", "adam-kowalski", "aigerim-bekova",
  "aisha-osei", "alex-johnson", "ali-hassan", "amina-diallo",
  "ana-pereira", "arjun-mehta", "asel-mamytbekova", "astrid-eriksson",
  "bjorn-lindqvist", "carlos-mendez", "chen-jing", "chen-xiaohan",
  "daniel-garcia", "dmitri-sokolov", "elena-volkov", "fatima-al-zahra",
  "grace-adeolu", "hana-novak", "huang-lei", "huang-zhixiang",
  "ivan-petrov", "james-okonkwo", "javier-santos", "ji-woo-lee",
  "jun-zhao", "kwame-asante", "layla-hassan", "lee-soo-yeon",
  "leo-weber", "lian-xiaoyu", "liang-jun", "lin-fang",
  "linh-nguyen", "marco-rossi", "mei-chen", "minh-thi-lan",
  "moon-hyun-jin", "nadia-larsson", "noa-kim", "omar-abdullahi",
  "park-so-yeon", "pavel-kozlov", "pham-thi-hoa", "preethi-nair",
  "priya-sharma", "rafael-costa", "rin-nakamura", "ryo-fujimoto",
  "sakura-ishida", "santiago-gomez", "sasha-petrov", "selin-kaya",
  "seun-adewale", "sofia-antonova", "takeshi-yamamoto", "thomas-muller",
  "tobias-schneider", "vikram-nair", "wang-jing", "wei-ziming",
  "wu-tianyi", "xu-meiying", "yu-jiali", "yuki-aoki",
  "yuna-kim", "zhang-yang", "zhao-ruiming", "zhen-wei",
  ...Array.from({ length: 100 }, (_, i) => `shop-${String(i + 1).padStart(3, "0")}`),
]);

/* ============================================================
   useLipsyncLiveKit — 5/18 P3 帧级流 hook (xiaocheng 专用)
   返 { connected, lkRoom, startAudio, attachVideo, sendAsk, lastSentence, danmuEvents }
   ============================================================ */
function useLipsyncLiveKit(slug, avatarId, personaSlug, voice) {  // 5/25 v10.75: 加 voice (roster.voice 真传给 daemon msg.voice, daemon 路由 sambert zhi*-v1 不再 fallback 聆小璇)
  const [connected, setConnected] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  // 5/18 v9.4 加 lkState 5 阶段可视化 (founder 实测黑屏/静态时定位卡哪一步)
  // idle → ws_connecting → ws_open → room_joining → room_joined → video_ready
  // 或 fail: ws_fail / room_fail / no_lib
  const [lkState, setLkState] = useState("idle");
  const wsRef = useRef(null);
  const lkRoomRef = useRef(null);
  // 5/18 multi-attach: 支持把 video track attach 到多个 <video> 元素 (InteractStage + VideoCallView modal)
  const videoElsRef = useRef([]);
  const videoTrackRef = useRef(null);
  const audioElsRef = useRef([]);
  const askCallbacksRef = useRef(new Map()); // ask_id → { onSentence, onDone, onError }
  const ourAskIdsRef = useRef(new Set());
  /* v10.6: WS 韧性 — server uvicorn ws_ping_interval=20 close 1011 反复重连. 不能让 UI 重渲 greet, 也不能丢 ask. */
  const hasGreetedRef = useRef(false);          // 真 server greet 已渲过, 重连后跳过
  const stableCidRef = useRef(null);             // 跨 reconnect 复用同 client_id (daemon side 可识别"老朋友")
  const pendingAsksRef = useRef([]);             // {text, persona, callbacks} — WS 重开后真重发

  useEffect(() => {
    if (!slug || !LIPSYNC_LIVEKIT_SLUGS.has(slug)) return;
    // 5/24 v10.63 GAN-R4-A — LK SDK 真 lazy load (省 92 KB gz first-paint).
    // window.__loadLiveKit() 真在 avatar.html head 定义 (interact mode 时已预 trigger).
    // 如真未 trigger (e.g., 用户从 live tab 切到 interact tab), 真这里 await.
    let cancelled = false;
    let currentWs = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let reconnectAttempt = 0;
    const ensureLkLoaded = async () => {
      if (typeof window === "undefined") return false;
      if (window.LivekitClient) return true;
      if (typeof window.__loadLiveKit === "function") {
        try {
          setLkState("lib_loading");
          await window.__loadLiveKit();
          return !!window.LivekitClient;
        } catch (e) {
          console.warn("[lk] __loadLiveKit failed", e);
          return false;
        }
      }
      return false;
    };
    /* v10.3: auto-reconnect on WS close (uvicorn protocol ping timeout / iOS Safari 节流). 5s 指数退避, 最多 30s. */
    const connect = () => {
      if (cancelled) return;
      /* v10.6: stable cid 跨 reconnect 复用 (daemon side 可识"老朋友" + 不重 greet). */
      if (!stableCidRef.current) {
        stableCidRef.current = `tv_${slug}_${Math.random().toString(36).slice(2, 10)}`;
      }
      const cid = stableCidRef.current;
      const wsUrl = `${LIPSYNC_WS_BASE}/${cid}?paradigm=livekit`;
      setLkState("ws_connecting");
      console.log("[lk] WS connecting" + (reconnectAttempt > 0 ? ` (reconnect #${reconnectAttempt}, cid=${cid})` : ` cid=${cid}`), wsUrl);
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      currentWs = ws;
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        reconnectAttempt = 0;
        setLkState("ws_open");
        console.log("[lk] WS open, sending switch_avatar", avatarId || "xc_L1", "voice=", voice || "(none)");
        ws.send(JSON.stringify({ type: "switch_avatar", avatar_id: avatarId || "xc_L1", persona_slug: personaSlug || slug || "", ...(voice ? { voice } : {}) }));
        /* v10.6: 重发 reconnect 期间堆的 pending ask, 让用户感受"WS 断了我重发我感知到" */
        if (pendingAsksRef.current.length > 0) {
          console.log(`[lk] resend ${pendingAsksRef.current.length} pending ask(s) after reconnect`);
          const pending = pendingAsksRef.current.slice();
          pendingAsksRef.current = [];
          /* defer 200ms 让 switch_avatar+livekit_room 先 settle */
          setTimeout(() => {
            for (const p of pending) {
              // 5/22 v10.28: 删 voice hardcode x6_lingxiaoxuan_pro — 让 daemon 按 persona_slug/aid 自查 SHOP_VOICE_MAP (sambert preset) 或 fallback xfyun.
              try { ws.send(JSON.stringify({ type: "ask", text: p.text, persona: p.persona || "", persona_slug: p.personaSlug || "", ask_id: p.askId, ...(p.voice ? { voice: p.voice } : (voice ? { voice } : {})) })); }  // 5/25 v10.76: ask 真带 voice 不再 fallback 聆小璇
              catch (e) { console.warn("[lk] resend fail", e); }
            }
          }, 200);
        }
      };
      ws.onmessage = async (e) => {
        if (e.data instanceof ArrayBuffer) return;
        let j;
        try { j = JSON.parse(e.data); } catch { return; }
        const t = j.type;
        if (t === "livekit_room") {
          console.log("[lk] livekit_room received, joining", j.url, j.room);
          setLkState("room_joining");
          try {
            const Room = window.LivekitClient.Room;
            const RoomEvent = window.LivekitClient.RoomEvent;
            // 5/25 v10.78: adaptiveStream true 致 16× 慢动作 — element 早期 0×0 时 sub 降级,
            // 元素上后追帧 1/16 速度. avatar 是单流 always-want, adaptive 无收益, 关掉.
            const room = new Room({ adaptiveStream: false, dynacast: false });
            lkRoomRef.current = room;
            room.on(RoomEvent.TrackSubscribed, (track) => {
              console.log("[lk] TrackSubscribed", track.kind);
              if (track.kind === "video") {
                videoTrackRef.current = track;
                videoElsRef.current.forEach(el => {
                  if (el) { try { track.attach(el); } catch {} }
                });
                setVideoReady(true);
                setLkState("video_ready");
              } else if (track.kind === "audio") {
                // 5/24 v10.61 P4 — LK Room 真预热: audio 真初始 muted=true 真静默载.
                // 真 startAudio() 真 unmute 在用户首交互 (tap/click) → 真无 iOS Safari autoplay block.
                // 真省: 用户首句 "你好" → first audio 真 -1.5s (现冷 LK connect 真已 warm).
                const a = document.createElement("audio");
                a.autoplay = true;
                a.muted = true;           // 真预热静默, 不爆音
                a.playsInline = true;     // iOS 真不全屏接管
                a.setAttribute("playsinline", "");
                a.preload = "auto";
                track.attach(a);
                document.body.appendChild(a);
                audioElsRef.current.push(a);
                // 真 startAudio() 真后续 unmute (v10.57 brute-force 真 unlock 已就位)
              }
            });
            room.on(RoomEvent.Disconnected, (r) => {
              console.warn("[lk] Room disconnected", r);
              if (!cancelled) { setConnected(false); setLkState("room_fail"); }
            });
            // 5/23 Stage B — SemCom v3 face latent DataChannel receiver (lab experiment, ?lab=1 to surface telemetry UI)
            // Wire: 28B header (BE) + 8192B fp16 latent = 8220B total
            //   2B MAGIC=0x4C41 ("LA") + 1B VERSION=3 + 1B TYPE=7 (LATENT_FACE_DECODE_FP16)
            //   2B frame_id + 2B payload_len + 8B utc_ms + 1B sub_type=0x05 + 1B reserved
            //   2B pad + 4×int16 bbox (x1,y1,x2,y2)
            const __semcomStats = { frames: 0, bytes: 0, firstTs: 0, lastTs: 0, lastBbox: null, lastFrameId: -1 };
            window.__semcomStats = __semcomStats;
            room.on(RoomEvent.DataReceived, (payload, participant, kind, topic) => {
              try {
                if (topic !== "face_latent") return;
                if (!(payload instanceof Uint8Array) || payload.length < 28) return;
                const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
                const magic = dv.getUint16(0, false); // BE
                if (magic !== 0x4C41) return;
                const version = dv.getUint8(2);
                const type_ = dv.getUint8(3);
                if (version !== 3 || type_ !== 7) return;
                const frameId = dv.getUint16(4, false);
                const payloadLen = dv.getUint16(6, false);
                const utcMs = Number(dv.getBigUint64(8, false));
                // bbox at offset 18-25 (2B pad then 4×int16)
                const bx1 = dv.getInt16(18, false), by1 = dv.getInt16(20, false);
                const bx2 = dv.getInt16(22, false), by2 = dv.getInt16(24, false);
                const latentBytes = payload.length - 28;
                __semcomStats.frames++;
                __semcomStats.bytes += payload.length;
                if (!__semcomStats.firstTs) __semcomStats.firstTs = utcMs;
                __semcomStats.lastTs = utcMs;
                __semcomStats.lastBbox = [bx1, by1, bx2, by2];
                __semcomStats.lastFrameId = frameId;
                // Emit log every 25 frames (~1s)
                if (__semcomStats.frames % 25 === 1) {
                  const elapsed = (utcMs - __semcomStats.firstTs) / 1000;
                  const fps = __semcomStats.frames / Math.max(elapsed, 0.001);
                  const kbps = (__semcomStats.bytes / Math.max(elapsed, 0.001)) / 1024;
                  console.log(`[semcom-v3] frame=${frameId} latent=${latentBytes}B bbox=(${bx1},${by1})-(${bx2},${by2}) fps=${fps.toFixed(1)} kbps=${kbps.toFixed(0)} totalFrames=${__semcomStats.frames}`);
                }
                if (window.__onSemcomFrame) {
                  window.__onSemcomFrame({ frameId, latentBytes, bbox: [bx1, by1, bx2, by2], utcMs, raw: payload.subarray(28) });
                }
              } catch (e) {
                console.warn("[semcom-v3] parse err", e);
              }
            });
            await room.connect(j.url, j.token);
            console.log("[lk] room.connect OK");
            setConnected(true);
            setLkState("room_joined");
          } catch (err) {
            console.warn("[lk] room connect failed", err);
            setLkState("room_fail");
          }
        } else if (t === "ask_started") {
          /* v10.6+: daemon 历史上覆盖 client ask_id 为自己生成的 (line 3686 已修但客户端兜底).
             如 daemon 真 fallback 自动生成, 用 ask_started 学到 daemon 的 ask_id, 找最新一个 pending callback re-key. */
          if (j.ask_id && !askCallbacksRef.current.has(j.ask_id)) {
            const ourKeys = Array.from(ourAskIdsRef.current);
            const lastOur = ourKeys[ourKeys.length - 1];
            if (lastOur && askCallbacksRef.current.has(lastOur)) {
              const cb = askCallbacksRef.current.get(lastOur);
              askCallbacksRef.current.set(j.ask_id, cb);
              console.log(`[lk] re-key callback ${lastOur} → daemon's ${j.ask_id}`);
            }
          }
        } else if (t === "sentence_start") {
          const cbs = askCallbacksRef.current.get(j.ask_id);
          if (cbs) cbs.onSentence?.(j.text || "", j.idx);
          /* v10.6: greet 去重 — server 每次 switch_avatar 都触发 greet (无 ask_id). 重连不能再渲. */
          else if (j.text && askCallbacksRef.current.size === 0 && !hasGreetedRef.current) {
            hasGreetedRef.current = true;
            if (window.__lkOnGreetSentence) window.__lkOnGreetSentence(j.text, avatarId);
          }
        } else if (t === "ask_done") {
          const cbs = askCallbacksRef.current.get(j.ask_id);
          if (cbs) cbs.onDone?.();
          askCallbacksRef.current.delete(j.ask_id);
          ourAskIdsRef.current.delete(j.ask_id);
        } else if (t === "error") {
          console.warn("[lk] daemon error", j.msg);
          askCallbacksRef.current.forEach((cbs) => cbs.onError?.(j.msg || "error"));
        }
      };
      ws.onclose = (ev) => {
        console.warn("[lk] WS close", ev.code, ev.reason);
        if (cancelled) return;
        setConnected(false); setLkState("ws_fail");
        try { if (pingTimer) clearInterval(pingTimer); pingTimer = null; } catch {}
        /* v10.3 auto-reconnect on unexpected close — 1000 (uvicorn idle close), 1006 (network), 1011 (keepalive timeout), 1005 (no status) 都重连 */
        if (ev.code === 1000 || ev.code === 1006 || ev.code === 1011 || ev.code === 1005) {
          reconnectAttempt++;
          const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(5, reconnectAttempt - 1)));
          console.log(`[lk] schedule reconnect #${reconnectAttempt} in ${delay}ms`);
          reconnectTimer = setTimeout(connect, delay);
        }
      };
      ws.onerror = (ev) => {
        console.warn("[lk] WS error", ev);
        if (!cancelled) { setConnected(false); setLkState("ws_fail"); }
      };

      /* v10.6: 客户端 app-level ping 8s — 比 server ws_ping_interval=20 紧 + 比 v10.3 15s 紧.
         server uvicorn 看到任何 frame 入 (我们 JSON ping 也算) 会复位 idle timer, 防 1011 keepalive timeout. */
      pingTimer = setInterval(() => {
        if (ws.readyState === 1) {
          try { ws.send(JSON.stringify({ type: "ping", t: Date.now() })); } catch {}
        }
      }, 8000);
    };
    // 5/24 v10.63 GAN-R4-A — 真 ensure LK loaded (lazy), 再 connect
    (async () => {
      const ready = await ensureLkLoaded();
      if (cancelled) return;
      if (!ready) {
        console.warn("[lk] LivekitClient not loaded (no __loadLiveKit fallback)");
        setLkState("no_lib");
        return;
      }
      connect();
    })();

    return () => {
      cancelled = true;
      try { if (pingTimer) clearInterval(pingTimer); } catch {}
      try { if (reconnectTimer) clearTimeout(reconnectTimer); } catch {}
      try { currentWs && currentWs.close(); } catch {}
      try { lkRoomRef.current?.disconnect(); } catch {}
      audioElsRef.current.forEach(a => { try { a.remove(); } catch {} });
      audioElsRef.current = [];
    };
  }, [slug, avatarId, voice]);  // 5/25 v10.75: voice 加入 deps, 切 shop 重发 switch_avatar 带新 voice

  const startAudio = useCallback(async () => {
    try {
      if (lkRoomRef.current && typeof lkRoomRef.current.startAudio === "function") {
        await lkRoomRef.current.startAudio();
      }
      audioElsRef.current.forEach(a => {
        a.muted = false; a.volume = 1;
        try { a.play().catch(() => {}); } catch {}
      });
    } catch (e) { console.warn("[lk] startAudio fail", e); }
  }, []);

  // 5/28 v10.86 hotfix — stage-controls volume button hook: mute/unmute 所有 LK 音频 el
  // 5/28 v10.90 — 保留 v10.86 hotfix outer try/catch (更稳)
  const setAudioMuted = useCallback((muted) => {
    try {
      audioElsRef.current.forEach(a => {
        a.muted = !!muted;
        if (!muted) { try { a.volume = 1; a.play().catch(() => {}); } catch {} }
      });
    } catch (e) { console.warn("[lk] setAudioMuted fail", e); }
  }, []);

  const sendAsk = useCallback((text, persona, callbacks) => {
    const ws = wsRef.current;
    const askId = `tvask_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    askCallbacksRef.current.set(askId, callbacks || {});
    ourAskIdsRef.current.add(askId);
    /* v10.6: 即使 WS 没 open 也接 ask — 推 pending 队列, ws.onopen 真重发. 不丢用户输入. */
    if (!ws || ws.readyState !== 1) {
      console.warn(`[lk] WS not open (state=${ws?.readyState}), queue ask for resend`);
      pendingAsksRef.current.push({ text, persona, personaSlug: personaSlug || slug || "", askId });
      return askId;
    }
    try {
      // 5/25 v10.76: ask payload 真带 voice (roster.voice → daemon msg.voice → sambert zhi*-v1 真路由)
      // 5/22 v10.28 old comment: 删 voice hardcode 让 daemon 按 aid 自查 — 但 5/25 daemon 改 priority on msg.voice, 必须显式传
      ws.send(JSON.stringify({
        type: "ask",
        text,
        persona: persona || "",
        persona_slug: personaSlug || slug || "",
        ask_id: askId,
        ...(voice ? { voice } : {}),
      }));
      return askId;
    } catch (e) {
      /* v10.6: send fail 也走 pending 队列等重连 */
      console.warn("[lk] send fail, queue ask for resend", e);
      pendingAsksRef.current.push({ text, persona, personaSlug: personaSlug || slug || "", askId, voice });
      return askId;
    }
  }, [voice]);  // 5/25 v10.76: voice 变 (founder 切 shop) 真重新闭包 sendAsk 带新 voice

  /* 5/19 v10.1 Phase B-1: 真中断 — daemon 已内置 {type:"interrupt"} handler (lipsync_daemon.py:3599).
     用户在小橙说话时讲新话 → 客户端先发 interrupt 砍 in-flight TTS+lipsync task, 再 sendAsk 新内容.
     daemon 端收 interrupt 时 session_active_task.cancel(), 立即停推流, 客户端 ack 后状态切回 listening. */
  const interrupt = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    try {
      ws.send(JSON.stringify({ type: "interrupt", t: Date.now() }));
      // 清掉所有 in-flight callbacks
      askCallbacksRef.current.forEach((cbs, id) => { try { cbs.onError?.("interrupted by user"); } catch {} });
      askCallbacksRef.current.clear();
      ourAskIdsRef.current.clear();
      console.log("[lk] sent interrupt");
      return true;
    } catch (e) {
      console.warn("[lk] interrupt fail", e);
      return false;
    }
  }, []);

  // multi-element attach: 同一 LiveKit video track 可同时 attach 到 InteractStage + VideoCallView
  const attachVideo = useCallback((el) => {
    if (!el) return;
    if (!videoElsRef.current.includes(el)) videoElsRef.current.push(el);
    // 如果 track 已就位, 立刻 attach (后挂载的元素也能补 hook 上 stream)
    if (videoTrackRef.current) {
      try { videoTrackRef.current.attach(el); } catch {}
    }
  }, []);
  const detachVideo = useCallback((el) => {
    if (!el) return;
    videoElsRef.current = videoElsRef.current.filter(x => x !== el);
    if (videoTrackRef.current) {
      try { videoTrackRef.current.detach(el); } catch {}
    }
  }, []);

  return { connected, videoReady, lkState, startAudio, setAudioMuted, sendAsk, interrupt, attachVideo, detachVideo };
}

// 5/18 v9.2.0: 替换 stale "23 顾客 ¥1280" 编造数据 (founder 5/18 早 demo 答非所问 root cause)
// 改 fetch 真 15KB persona (MarketOps §0-§11 + 3 SKU + 4 角色路由 + 8 few-shot + 8 rules + tools).
// 用绝对路径避免相对 path 在不同 entry 下解析不一致.
// 5/28 v10.83: 仅 xiaocheng slug 时 fetch persona (其他 slug 不消费 XIAOCHENG_PERSONA, 省 9KB)
let XIAOCHENG_PERSONA = "你是万物小店数字店长小橙. 等待真 persona 加载中...";
(async function loadXcPersona() {
  try {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('slug') || '';
    if (slug && slug !== 'xiaocheng') return;  // skip — 非 xc 不消费
    const r = await fetch("/xc_persona_xiaocheng.md", { cache: "no-cache" });
    if (r.ok) {
      const txt = await r.text();
      if (txt && txt.length > 1000) {
        XIAOCHENG_PERSONA = txt;
        console.log(`[xc] real persona loaded: ${txt.length} chars`);
      }
    }
  } catch (e) { console.warn("[xc] persona fetch fail, fallback", e); }
})();

/* ============================================================
   useChatSocket — slug-keyed WS hook
   ============================================================ */
function useChatSocket(slug) {
  // status: connecting | open | closed | error
  const [status, setStatus] = useState("connecting");
  const [greeting, setGreeting] = useState(null);
  const wsRef = useRef(null);
  const pendingRef = useRef(new Map());   // ask_id -> { onToken, onDone, onError, firstToken }
  const reconnectAttemptRef = useRef(0);
  const closedByUserRef = useRef(false);

  useEffect(() => {
    if (!slug) return;
    closedByUserRef.current = false;
    let cancelled = false;

    function connect() {
      if (cancelled) return;
      setStatus("connecting");
      const ws = new WebSocket(`${INTERACT_WS_BASE}?slug=${encodeURIComponent(slug)}`);
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        if (cancelled) return;
        reconnectAttemptRef.current = 0;
        setStatus("open");
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== "string") return; // ignore keepalive bytes
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        const t = m.type;
        if (t === "hello") {
          setGreeting({ slug: m.slug, name: m.name, greeting: m.greeting, model: m.model });
          return;
        }
        if (t === "pong") return;
        const pend = m.ask_id ? pendingRef.current.get(m.ask_id) : null;
        if (!pend) return;
        if (t === "ask_started") { pend.startedAt = performance.now(); return; }
        if (t === "first_token") {
          pend.firstTokenMs = m.first_token_ms;
          pend.onFirstToken?.(m.first_token_ms);
          return;
        }
        if (t === "token") { pend.onToken?.(m.text); return; }
        if (t === "done") {
          pendingRef.current.delete(m.ask_id);
          pend.onDone?.({
            text: m.text,
            firstTokenMs: pend.firstTokenMs ?? m.first_token_ms ?? null,
            totalMs: m.total_ms,
          });
          return;
        }
        if (t === "error") {
          pendingRef.current.delete(m.ask_id);
          pend.onError?.(m.msg || "上游服务忙,稍后再试");
          return;
        }
      };
      ws.onerror = () => { setStatus("error"); };
      ws.onclose = () => {
        if (cancelled || closedByUserRef.current) return;
        setStatus("closed");
        // exponential-ish reconnect: 1s, 2s, 4s, capped 10s
        const wait = Math.min(10000, 1000 * 2 ** reconnectAttemptRef.current);
        reconnectAttemptRef.current += 1;
        setTimeout(connect, wait);
      };
    }
    connect();

    return () => {
      cancelled = true;
      closedByUserRef.current = true;
      try { wsRef.current && wsRef.current.close(); } catch {}
      pendingRef.current.forEach((p) => p.onError?.("会话已断开"));
      pendingRef.current.clear();
    };
  }, [slug]);

  const send = useCallback((text, callbacks) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) {
      callbacks?.onError?.("尚未连接");
      return null;
    }
    const askId = `ask_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    pendingRef.current.set(askId, {
      onFirstToken: callbacks?.onFirstToken,
      onToken: callbacks?.onToken,
      onDone: callbacks?.onDone,
      onError: callbacks?.onError,
    });
    try {
      ws.send(JSON.stringify({ type: "say", text, ask_id: askId }));
    } catch (e) {
      pendingRef.current.delete(askId);
      callbacks?.onError?.(String(e));
      return null;
    }
    return askId;
  }, []);

  return { status, greeting, send };
}

/* ============================================================
   defaultDanmu — used only as offline fallback if WS never opens
   ============================================================ */
function defaultDanmu(avatar) {
  const name = avatar?.name || "小橙";
  return [
    { id: 1, mine: false, user: name, avatar: name[0] || "·", t: "刚刚",
      q: null, a: `嗨,我是 ${name}。点这里发消息,就能跟我聊起来。` },
  ];
}

/* ============================================================
   Interact Room — root of 互动 tab
   ============================================================ */
function InteractRoom({ avatar }) {
  const initialMode = LIPSYNC_LIVEKIT_SLUGS.has(avatar?.slug || "xiaocheng") ? "voice" : "text";
  const [mode, setMode] = useState(initialMode);          // 5/28 v10.81: LIPSYNC slug 默认 voice (auto LK 视频+音频), 其他 slug 仍 text
  const [text, setText] = useState("");
  const [danmu, setDanmu] = useState(() => defaultDanmu(avatar));
  const [queue, setQueue] = useState(0);              // 真实排队 (后端推送, M3+)
  const [recording, setRecording] = useState(false);
  const [recCancel, setRecCancel] = useState(false);
  const [videoCall, setVideoCall] = useState(false);
  const [videoPermAsked, setVideoPermAsked] = useState(false);
  const [greetedAt, setGreetedAt] = useState(null);
  // 5/18 v9.6 真因修: iOS Safari 点 [语音] tab → setMode("voice") → React 立即挂 mic button →
  // 同一 touch 的 pointerdown LEAK 到新挂载的 button → 自动触发 voiceDown → getUserMedia 弹权限.
  // founder 实测: 点语音后立即弹"Allow Microphone"对话框, Allow 后黑屏(button 在 recording 中, 等不到 pointerup).
  // 修: voice mode 挂载时间戳记录, voiceDown 前 400ms 守卫忽略 stale pointerdown.
  const voiceMountedAtRef = useRef(0);
  useEffect(() => {
    if (mode === "voice") voiceMountedAtRef.current = Date.now();
  }, [mode]);

  // 5/28 v10.84 — stage-subtitle overlay: 当前正在说的句子, sentence_start 推, ~4s 自动淡出
  const [currentSentence, setCurrentSentence] = useState("");
  const subtitleClearTimerRef = useRef(null);

  const slug = avatar?.slug || "xiaocheng";
  const useLiveKit = LIPSYNC_LIVEKIT_SLUGS.has(slug);
  // 5/28 founder rule: L20 退役 → H200 按需开. Text mode 全 slug (含 xiaocheng + 100 shop)
  // 走 /ws/interact (DashScope qwen3.7-max-2026-05-20 秒回), 不依赖 lipsync.
  // Voice/video mode 才开 LK — H200 daemon LIVE 时真嘴动, down 时 fallback simulateReply.
  const lkActive = useLiveKit && mode !== "text";

  // 5/18 P3: xiaocheng 走 lipsync_daemon 帧级流 (真嘴动 + 真声音).
  // 5/28: text mode 全 slug 走 /ws/interact (interact_chat:3146 qwen3.7-max), 不再 ternary skip xiaocheng/shop.
  const { status, greeting, send: wsSend } = useChatSocket(slug);
  /* 5/21 v10.18 Phase E: avatar_id = slug → daemon lazy-prep from /data/motion_pool/motion_lp_<slug>.mp4.
     首次 30-90s (mmpose+MuseTalk latent extract), 之后 cached. 真显 shop 自己脸. */
  const lk = useLipsyncLiveKit(lkActive ? slug : null, slug, slug, avatar?.voice);  // 5/25 v10.75: 传 roster voice (shop-001 → zhihao-v1); 5/28: text mode 不开 LK 避 lipsync 404 noise
  const [unmuted, setUnmuted] = useState(false);

  // 5/28 v10.90 rebase — currentSentence + subtitleClearTimerRef 已在上方 (line ~592, v10.84 真声明).
  // v10.86 hotfix 假以为 v10.85 漏声明而重声明, rebase 后变 duplicate, 此处删之.

  /* 5/21 v10.21 text-mode browser TTS fallback (probe-group commit 9f15b13 pattern):
     text mode 下用户没必要听 LiveKit 音频流 (要 unmute + 等 video track), 但希望"听到回答".
     SpeechSynthesis 是 0-network 0-latency. 仅 mode==='text' 时用 — voice/video 仍走 LK 音频. */
  const ttsGenderRef = useRef("F");
  useEffect(() => {
    // sniff gender from persona_slug — shop-NNN roster 里有真值, 这里粗估: M 开头 / male / 男声 → 男; 否则女
    const g = (avatar?.gender || avatar?.voice_gender || avatar?.id || avatar?.slug || "").toString();
    ttsGenderRef.current = /^[Mm]|male|男/.test(g) ? "M" : "F";
  }, [avatar?.id, avatar?.slug, avatar?.gender, avatar?.voice_gender]);

  const speakLocal = useCallback((textToSpeak) => {
    if (!textToSpeak) return;
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    try {
      const u = new window.SpeechSynthesisUtterance(String(textToSpeak));
      u.lang = "zh-CN";
      u.pitch = ttsGenderRef.current === "M" ? 0.92 : 1.05;
      u.rate = 1.05;
      window.speechSynthesis.speak(u);
    } catch (e) { /* swallow */ }
  }, []);

  const cancelLocalTTS = useCallback(() => {
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch (e) { /* swallow */ }
  }, []);

  // mode 切换时 stop in-flight TTS (避 text mode 残留语音继续在 voice/video mode 播)
  useEffect(() => {
    cancelLocalTTS();
  }, [mode, cancelLocalTTS]);

  /* v10.5: 自动 unmute — 用户已点 "互动" tab 进来 = user gesture, 浏览器允许 startAudio.
     LK videoReady 触发后立即静默调 startAudio. 失败 (Safari 严格 autoplay) 浏览器自显原生提示. */
  useEffect(() => {
    if (!useLiveKit || !lk.videoReady || unmuted) return;
    try { lk.startAudio?.(); } catch {}
    setUnmuted(true);
  }, [useLiveKit, lk.videoReady, unmuted]);

  /* v10.57: tap-anywhere fallback v2 — iOS Safari/Chrome silent reject autoplay.
     lk.startAudio() may return without error but browser still blocks <audio>.play().
     Brute-force: iterate ALL <audio> elements on page + a.muted=false + a.play().
     Retries on EACH gesture until unmuted (no once:true). */
  useEffect(() => {
    if (!useLiveKit || unmuted) return;
    const onGesture = () => {
      try { lk.startAudio?.(); } catch {}
      // Force-play every audio element + unmute
      document.querySelectorAll("audio").forEach((a) => {
        try { a.muted = false; a.play().catch(() => {}); } catch {}
      });
      // Only flip unmuted state once at least one audio is actually playing
      const anyPlaying = Array.from(document.querySelectorAll("audio")).some((a) => !a.paused && a.readyState > 2);
      if (anyPlaying) setUnmuted(true);
    };
    document.addEventListener("click", onGesture, { capture: true });
    document.addEventListener("touchstart", onGesture, { capture: true, passive: true });
    return () => {
      document.removeEventListener("click", onGesture, { capture: true });
      document.removeEventListener("touchstart", onGesture, { capture: true });
    };
  }, [useLiveKit, unmuted]);

  // 收到 greeting 后, 替换首张占位 card 为真欢迎语
  useEffect(() => {
    if (!greeting) return;
    setDanmu([{
      id: `greet-${Date.now()}`,
      mine: false,
      user: greeting.name,
      avatar: greeting.name?.[0] || "·",
      t: "刚刚",
      q: null,
      a: greeting.greeting,
    }]);
    setGreetedAt(Date.now());
    // 5/22 v10.26: greeting 不走 browser TTS — legacy WS path 也通过 LK 音频. 不让浏览器女声叠加.
  }, [greeting]);

  // visualViewport — detect keyboard up on mobile
  useEffect(() => {
    if (!window.visualViewport) return;
    const onResize = () => {
      const h = window.visualViewport.height;
      const kbUp = h < window.innerHeight * 0.78;
      document.body.classList.toggle("kb-up", kbUp);
    };
    window.visualViewport.addEventListener("resize", onResize);
    return () => window.visualViewport.removeEventListener("resize", onResize);
  }, []);

  // auto-scroll danmu
  const danmuRef = useRef(null);
  useEffect(() => {
    const el = danmuRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [danmu.length]);

  function ask(v) {
    if (!v) return;
    const askCardId = `q-${Date.now()}`;
    const replyCardId = `a-${Date.now()}`;
    const startedT = performance.now();
    setDanmu(d => [
      ...d,
      { id: askCardId, mine: true, user: "我", avatar: "我", t: "刚刚", q: v, a: null, isQ: true },
      { id: replyCardId, mine: false,
        user: (useLiveKit ? avatar?.name : (greeting?.name || avatar?.name)) || "TA",
        avatar: ((useLiveKit ? avatar?.name : (greeting?.name || avatar?.name)) || "·")[0],
        t: "正在回复…", q: null, a: "", streaming: true, firstTokenMs: null },
    ]);

    // === xiaocheng + shop-* LiveKit path (voice/video only): lipsync_daemon 同步推 sentence + frames + audio ===
    // 5/28: text mode 全 slug 走老 WS path (interact_chat:3146 qwen3.7-max), 不再 LK-route text.
    if (useLiveKit && mode !== "text") {
      if (!lk.connected) {
        setTimeout(() => {
          const fallback = simulateReply(v, avatar);
          setDanmu(d => d.map(x => x.id === replyCardId
            ? { ...x, a: fallback, streaming: false, t: "刚刚", offline: true } : x));
        }, 1200);
        return;
      }
      let acc = "";
      lk.sendAsk(v, XIAOCHENG_PERSONA, {
        onSentence: (text, idx) => {
          acc += (idx === 0 ? "" : " ") + (text || "");
          // 5/28 v10.84 — push current sentence to stage-subtitle overlay
          if (text) pushSubtitle(text);
          if (idx === 0) {
            const ttft = Math.round(performance.now() - startedT);
            setDanmu(d => d.map(x => x.id === replyCardId
              ? { ...x, a: acc, firstTokenMs: ttft } : x));
          } else {
            setDanmu(d => d.map(x => x.id === replyCardId ? { ...x, a: acc } : x));
          }
          // 5/22 v10.26: 禁 browser TTS — 它默认中文女声覆盖 daemon 真 sambert 男声 (founder
          // 实测 shop-020 顾大壮 男声听到女声). LK audio (track subscribed) 是真值, 让它播.
          // 老 5/21 v10.21 推断 "text 模式 LK audio 没 attach" 错; 实际 LK audio 永远 attach,
          // 只是默认 muted 直到 lk.startAudio. 该 unmute 调用在 useLiveKit ? lk.videoReady 触发.
        },
        onDone: () => {
          setDanmu(d => d.map(x => x.id === replyCardId
            ? { ...x, a: acc || x.a || "", streaming: false, t: "刚刚" } : x));
        },
        onError: (msg) => {
          setDanmu(d => d.map(x => x.id === replyCardId
            ? { ...x, a: x.a || "(暂时连不上,稍后再试)", streaming: false, t: "刚刚", error: msg } : x));
        },
      });
      return;
    }

    // === Non-xiaocheng slug: legacy text WS ===
    if (status !== "open") {
      setTimeout(() => {
        const fallback = simulateReply(v, avatar);
        setDanmu(d => d.map(x => x.id === replyCardId
          ? { ...x, a: fallback, streaming: false, t: "刚刚", offline: true } : x));
      }, 1200);
      return;
    }
    wsSend(v, {
      onFirstToken: (ms) => {
        setDanmu(d => d.map(x => x.id === replyCardId ? { ...x, firstTokenMs: ms } : x));
      },
      onToken: (tok) => {
        setDanmu(d => d.map(x => x.id === replyCardId
          ? { ...x, a: (x.a || "") + tok } : x));
      },
      onDone: ({ text: full, firstTokenMs, totalMs }) => {
        setDanmu(d => d.map(x => x.id === replyCardId
          ? { ...x, a: full || x.a || "", streaming: false, t: "刚刚",
              firstTokenMs, totalMs } : x));
        // 5/22 v10.26: 禁 browser TTS (覆盖 daemon 真 voice)
      },
      onError: (msg) => {
        setDanmu(d => d.map(x => x.id === replyCardId
          ? { ...x, a: (x.a || "(暂时连不上,稍后再试)"),
              streaming: false, t: "刚刚", error: msg } : x));
      },
    });
  }

  function send() {
    const v = text.trim();
    if (!v) return;
    if (v.length > 100) return;
    ask(v);
    setText("");
  }


  function onKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  }

  // 5/18 v9.2.1 voice — server-side 真讯飞 IAT (founder 历史路径, wanwu-chat-bridge /stt).
  // 旧 Web Speech API 在 iOS Safari 不识别中文, founder 实测黑屏 + 听不到.
  // 改: MediaRecorder 录 webm/opus → POST https://wanwushop.cn/proai/stt → 拿真识别文字 → ask().
  //
  // 5/18 v9.4 STT 默认切 Higgs Audio v3 (Apache-2.0 自托管 GPU :3147).
  // 老 xfyun /proai/stt 5/18 实证 404 死 (Java BFF 路由已下), 默认走它 = 语音永远无响应 = "黑屏感".
  // ?stt=xfyun URL 参数可临时回切 (老 chain 复活后用).
  // 返回 shape 与 xfyun 兼容 ({data:{text}, text, latency_ms, backend}).
  const _sttPref = (typeof window !== "undefined")
    ? (new URL(window.location.href).searchParams.get("stt") || "higgs").toLowerCase()
    : "higgs";
  const STT_ENDPOINT = _sttPref === "xfyun"
    ? "https://wanwushop.cn/proai/stt"
    : "https://radio.wanwushop.cn/stt/transcribe";
  const STT_BACKEND_LABEL = _sttPref === "xfyun" ? "讯飞 IAT" : "Higgs v3 STT";
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  async function voiceDown(e) {
    e.preventDefault();
    // 5/18 v9.6: 守卫 voice mode 刚挂载时 iOS Safari 触摸事件 leak 的假触发
    const sinceMount = Date.now() - voiceMountedAtRef.current;
    if (sinceMount < 400) {
      console.warn("[voice] ignore stale pointerdown", sinceMount, "ms after mount");
      return;
    }
    setRecording(true);
    audioChunksRef.current = [];
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("浏览器不支持 mic API");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      audioStreamRef.current = stream;
      // 选浏览器支持的格式 (Chrome/Edge: webm/opus; iOS Safari: mp4/aac)
      const mime = MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                 : MediaRecorder.isTypeSupported?.("audio/mp4") ? "audio/mp4"
                 : MediaRecorder.isTypeSupported?.("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecRef.current = rec;
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      rec.start();
    } catch (err) {
      setRecording(false);
      setDanmu(d => [...d, { id: `voice-perm-${Date.now()}`, mine: false,
        user: "系统", avatar: "!", t: "刚刚", q: null,
        a: "麦克风没拿到 (" + (err.message || err.name) + "). 浏览器地址栏左边图标 → 允许麦克风 → 再试一次." }]);
    }
  }

  function voiceMove(e) {
    if (!recording) return;
    const touch = e.touches?.[0] || e;
    const btn = e.currentTarget.getBoundingClientRect();
    const dy = (touch.clientY ?? 0) - btn.top;
    setRecCancel(dy < -60);
  }

  async function voiceUp() {
    if (!recording) return;
    setRecording(false);
    const rec = mediaRecRef.current;
    const stream = audioStreamRef.current;
    const wasCancel = recCancel;
    setRecCancel(false);
    try { rec && rec.state !== "inactive" && rec.stop(); } catch {}
    // 等 ondataavailable + onstop 落定
    await new Promise(r => setTimeout(r, 150));
    try { stream && stream.getTracks().forEach(t => t.stop()); } catch {}
    audioStreamRef.current = null;
    mediaRecRef.current = null;
    if (wasCancel) { audioChunksRef.current = []; return; }
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    if (!chunks.length) {
      setDanmu(d => [...d, { id: `voice-empty-${Date.now()}`, mine: false,
        user: "系统", avatar: "!", t: "刚刚", q: null, a: "没录到音频. 再按住麦克风长一点。" }]);
      return;
    }
    const blob = new Blob(chunks, { type: chunks[0].type || "audio/webm" });
    if (blob.size < 800) {
      setDanmu(d => [...d, { id: `voice-short-${Date.now()}`, mine: false,
        user: "系统", avatar: "!", t: "刚刚", q: null, a: "音频太短 (" + blob.size + " B). 按住说话保持 1s 以上。" }]);
      return;
    }
    // 显示"识别中"占位卡
    const sttCardId = `stt-${Date.now()}`;
    setDanmu(d => [...d, { id: sttCardId, mine: true, user: "我", avatar: "我", t: "识别中…",
                            q: "🎤 (录音 " + (blob.size/1024).toFixed(1) + "KB)", a: null, sttPending: true }]);
    try {
      const form = new FormData();
      form.append("audio", blob, "voice." + (blob.type.includes("webm") ? "webm" : blob.type.includes("mp4") ? "m4a" : "ogg"));
      const r = await fetch(STT_ENDPOINT, { method: "POST", body: form, credentials: "omit" });
      const j = await r.json();
      /* v10.14: 兼容 DashScope cascade 返回 j.text (顶层) + 老 xfyun j.data.text (nested) */
      const text = (j && (j.text || j.data?.text) || "").trim();
      if (!text) {
        setDanmu(d => d.map(x => x.id === sttCardId
          ? { ...x, q: "🎤 没识别到内容", a: "请再按住麦克风说清楚一点。" } : x));
        return;
      }
      // 把识别到的文字替换到 STT 卡, 然后真 ask
      setDanmu(d => d.filter(x => x.id !== sttCardId));
      ask(text);
    } catch (err) {
      setDanmu(d => d.map(x => x.id === sttCardId
        ? { ...x, q: "🎤 STT 服务异常", a: `${STT_BACKEND_LABEL} 没响应: ` + (err.message || err.name) } : x));
    }
  }

  function openVideoCall() {
    if (!videoPermAsked) {
      setVideoPermAsked(true);
      return;
    }
    setVideoCall(true);
  }

  // 5/28 v10.84 — stage-subtitle helper: push 一句到 video overlay, 4s 后自动清空 (除非下句覆盖)
  const pushSubtitle = useCallback((text) => {
    if (!text) return;
    setCurrentSentence(text);
    if (subtitleClearTimerRef.current) clearTimeout(subtitleClearTimerRef.current);
    subtitleClearTimerRef.current = setTimeout(() => { setCurrentSentence(""); }, 4000);
  }, []);

  // 5/18 P3: greet sentences from lipsync_daemon go to danmu (proactive greet path)
  useEffect(() => {
    if (!useLiveKit) return;
    let acc = "";
    let cardCreated = false;
    window.__lkOnGreetSentence = (text, avatarId) => {
      acc += (acc ? " " : "") + text;
      // 5/28 v10.84 — 同步推到 stage-subtitle overlay
      pushSubtitle(text);
      if (!cardCreated) {
        cardCreated = true;
        const name = avatar?.name || "小橙";
        setDanmu([{
          id: `greet-${Date.now()}`, mine: false,
          user: name, avatar: name[0] || "·",
          t: "刚刚", q: null, a: acc,
        }]);
      } else {
        setDanmu(d => d.map((x, i) => i === d.length - 1 ? { ...x, a: acc } : x));
      }
    };
    return () => { delete window.__lkOnGreetSentence; };
  }, [useLiveKit, avatar?.name, pushSubtitle]);

  function handleUnmute() {
    setUnmuted(true);
    if (useLiveKit) lk.startAudio();
  }

  return (
    <div className="interact-region">
      <div className="interact-room">
        {/* —————— stage + composer —————— */}
        <div className="interact-main">
          <InteractStage
            avatar={avatar}
            useLiveKit={useLiveKit}
            videoReady={lk.videoReady}
            lkState={lk.lkState}
            attachVideo={lk.attachVideo}
            unmuted={unmuted}
            onUnmute={handleUnmute}
            currentSentence={currentSentence}
            setAudioMuted={lk.setAudioMuted}
            onSwitchToVoiceTab={() => setMode("voice")}
            onEndCall={() => { setCurrentSentence(""); setMode("text"); }}
          />

          <div className="interact-composer">
            <div className="composer-tabs" role="tablist">
              {[
                { id: "text",  label: "文字", icon: "type" },
                { id: "voice", label: "语音", icon: "mic" },
                { id: "video", label: "视频", icon: "video" },
              ].map(m => (
                <button
                  key={m.id}
                  role="tab"
                  aria-selected={mode === m.id}
                  className={`composer-tab ${mode === m.id ? "active" : ""}`}
                  onClick={() => m.id === "video" ? openVideoCall() : setMode(m.id)}
                >
                  <i data-lucide={m.icon}></i>
                  <span>{m.label}</span>
                </button>
              ))}
              {queue > 0 && (
                <div className="composer-queue">
                  <span className="queue-dot"/>
                  排队 · 前面 <strong>{queue}</strong> 人 · 预计 <strong>{queue * 4}s</strong>
                </div>
              )}
            </div>

            {mode === "text" && (
              <div className="composer-text">
                <textarea
                  name="ask-input"
                  id="ask-input"
                  aria-label="向数字人提问"
                  rows={1}
                  value={text}
                  maxLength={100}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={`向 ${avatar?.name || "TA"} 提问 · Enter 发送`}
                />
                <div className="composer-text-counter">
                  <span style={{ color: text.length > 80 ? "var(--mk-accent)" : undefined }}>{text.length}</span>
                  <span>/100</span>
                </div>
                <button
                  className={`composer-send ${text.trim() ? "active" : ""}`}
                  onClick={send}
                  disabled={!text.trim()}
                  aria-label="发送"
                >
                  <i data-lucide="send-horizontal"></i>
                </button>
              </div>
            )}

            {mode === "voice" && (
              <div className="composer-voice">
                <button
                  className={`voice-big ${recording ? "recording" : ""} ${recCancel ? "cancel" : ""}`}
                  onPointerDown={voiceDown}
                  onPointerMove={voiceMove}
                  onPointerUp={voiceUp}
                  onPointerCancel={voiceUp}
                >
                  {recording ? (
                    <div className="voice-waveform">
                      {Array.from({length: 8}).map((_, i) => (
                        <span key={i} style={{ animationDelay: `${i * 0.08}s` }}/>
                      ))}
                    </div>
                  ) : (
                    /* 5/18 v9.7 真因修: 老 <i data-lucide="mic"> 被 lucide.js 替成 <svg> 后,
                       recording 切换让 React 试图删原 <i> 但 DOM 节点已变 → WebKit 严格报
                       "The object can not be found here" → 整个 InteractRoom 崩 → 黑屏.
                       Chromium 容错跑过, Safari 真炸. 改内联 SVG, React 全权管 DOM. */
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="22"/>
                    </svg>
                  )}
                  <span className="voice-hint">
                    {recording
                      ? (recCancel ? "上滑取消" : "松开发送 · 上滑取消")
                      : "按住说话 · 200ms 触发"}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* —————— danmu side panel —————— */}
        <div className="interact-chat-panel">
          <div className="chat-panel-head">
            <i data-lucide="message-square-text"></i>
            <span>弹幕互动</span>
            <span className="chat-live-dot"/>
          </div>
          <div className="chat-list" ref={danmuRef}>
            {danmu.map(d => <DanmuCard key={d.id} item={d} />)}
          </div>
          <div className="chat-panel-foot">
            <i data-lucide="info"></i>
            <span>10 分钟无互动将自动切回直播</span>
          </div>
        </div>
      </div>

      {/* video call permission gate */}
      {videoPermAsked && !videoCall && (
        <VideoPermDialog
          avatar={avatar}
          onCancel={() => setVideoPermAsked(false)}
          onAccept={() => setVideoCall(true)}
        />
      )}
      {videoCall && (
        <VideoCallView
          avatar={avatar}
          onHangup={() => { setVideoCall(false); setVideoPermAsked(false); }}
          /* pass lk hook + persona so modal renders 真嘴动 + 用同款 LLM */
          lkAttachVideo={useLiveKit ? lk.attachVideo : null}
          lkDetachVideo={useLiveKit ? lk.detachVideo : null}
          lkVideoReady={useLiveKit ? lk.videoReady : false}
          lkSendAsk={useLiveKit ? lk.sendAsk : null}
          lkInterrupt={useLiveKit ? lk.interrupt : null}
          lkStartAudio={useLiveKit ? lk.startAudio : null}
          /* 5/18 v9.4 bug-B: 视频通话内 push-to-talk 真接 Higgs STT → lkSendAsk → 小橙真回 */
          sttEndpoint={STT_ENDPOINT}
          sttBackendLabel={STT_BACKEND_LABEL}
          personaText={useLiveKit ? XIAOCHENG_PERSONA : ""}
          onDanmu={(card) => setDanmu(d => [...d, card])}
          updateDanmu={(id, patch) => setDanmu(d => d.map(x => x.id === id ? { ...x, ...patch } : x))}
        />
      )}
    </div>
  );
}

function simulateReply(q, avatar) {
  const name = avatar?.name || "小橙";
  if (/(订|位|booking|预订)/.test(q)) return `好的,我已为你保留位置。需要发短信提醒吗?`;
  if (/(几点|when|时间)/.test(q)) return `营业时间 10:00 — 凌晨 2:00,今天人比较多,建议提前到。`;
  if (/(多少|价格|price)/.test(q)) return `招牌锅 ¥128 起,人均 ¥168。今日有 88 折活动。`;
  if (q.length < 10) return `好的,${name} 已经记下,稍后给你详细回复。`;
  return `这个问题不错。简短回答:基于现状,我建议你优先考虑性价比与稳定性两方面。需要更深入展开吗?`;
}

/* ============================================================
   Stage — 9:16 talking-head video region
   5/18 P3: xiaocheng 走 LiveKit <video> 真嘴动, 其他 slug 仍 static
   ============================================================ */
function InteractStage({ avatar, useLiveKit, videoReady, lkState, attachVideo, unmuted, onUnmute,
                          currentSentence, setAudioMuted, onSwitchToVoiceTab, onEndCall }) {  // 5/28 v10.84: 加 subtitle + controls
  const videoRef = useRef(null);
  // 5/28 v10.84 — stage-controls 状态
  const [audioMuted, setAudioMutedState] = useState(false);  // 默认 unmuted (LK videoReady 后已 startAudio)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const stageVideoRef = useRef(null);
  const toggleAudioMute = useCallback(() => {
    const next = !audioMuted;
    setAudioMutedState(next);
    if (setAudioMuted) setAudioMuted(next);
  }, [audioMuted, setAudioMuted]);
  const toggleFullscreen = useCallback(() => {
    const el = stageVideoRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);
  useEffect(() => {
    if (useLiveKit && attachVideo && videoRef.current) {
      attachVideo(videoRef.current);
    }
  }, [useLiveKit, attachVideo]);

  // 5/23 v10.53 — 真值回正: LK H264 server-render 是 DEFAULT (生产级真完整无损).
  //   客户端 SemCom v3 decode 真改 opt-in (paradigm 真值在规模, 不在单 user 视觉).
  //   v10.45-v10.51 真默认切 client decode 真激进 — user 真感知 Q LPIPS 0.039 降质,
  //   但 daemon 真双轨推 LK + latent → server 真没省一分带宽 → 真切默认无意义.
  //
  //   ?paradigm=client / ?lab=2 → opt-in client decode (开发者 / 真规模 R&D)
  //   ?paradigm=lk            → 真显 force LK (兼容)
  //   ?lab=1                  → telemetry only (no decode)
  //   ?dev=1                  → telemetry overlay (decode 真按其他 flag)
  const urlParams = useMemo(() => new URL(window.location.href).searchParams, []);
  const labParam = urlParams.get("lab") || "";
  const paradigmParam = (urlParams.get("paradigm") || "").toLowerCase();
  const devMode = urlParams.get("dev") === "1" || labParam === "1" || labParam === "2";
  const optInClient = paradigmParam === "client" || labParam === "2";
  const webgpuAvailable = typeof navigator !== "undefined" && !!navigator.gpu;
  // v10.53: 真值回正 — 客户端 decode 真 opt-in only.
  const labDecode = optInClient && webgpuAvailable;
  const labMode = devMode;
  const [semStats, setSemStats] = useState({ frames: 0, bytes: 0, fps: 0, kbps: 0, bbox: null, frameId: -1 });
  const [decoderState, setDecoderState] = useState({ ready: false, decoded: 0, avgMs: 0, error: null });
  const decodeCanvasRef = useRef(null);
  // Decoder is "primary" (replaces LK video as face renderer) when ready + no failure + not opted out.
  const decoderPrimary = labDecode && decoderState.ready && !decoderState.error;

  useEffect(() => {
    if (!labMode) return;
    const id = setInterval(() => {
      const s = window.__semcomStats || {};
      const elapsed = (s.lastTs - s.firstTs) / 1000;
      const fps = elapsed > 0 ? s.frames / elapsed : 0;
      const kbps = elapsed > 0 ? (s.bytes / elapsed) / 1024 : 0;
      setSemStats({ frames: s.frames || 0, bytes: s.bytes || 0, fps, kbps,
                    bbox: s.lastBbox, frameId: s.lastFrameId });
      const v = window.__semcomVaeStats;
      if (v) {
        setDecoderState({
          ready: !!v.session,
          decoded: v.framesDecoded || 0,
          avgMs: v.framesDecoded > 0 ? (v.totalDecodeMs / v.framesDecoded) : 0,
          tCopy: v.tCopy || 0,
          tRun: v.tRun || 0,
          tPost: v.tPostProc || 0,
          tDraw: v.tDraw || 0,
          error: null,
        });
      }
    }, 500);
    return () => clearInterval(id);
  }, [labMode]);

  // v10.45 — auto-load WebGPU VAE decoder (default for all users with WebGPU).
  // Falls back to LK video on any failure (no regression for unsupported browsers).
  useEffect(() => {
    if (!labDecode) return;
    let cancelled = false;
    (async () => {
      try {
        if (!window.SemcomVaeDecoder) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "semcom_vae_decoder.js?v=10.52";
            s.onload = resolve;
            s.onerror = () => reject(new Error("vae decoder script load fail"));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        window.__semcomPersonaSlug = avatar?.slug || "";
        // Force ?student=q (Stage E-Q channels-quarter) when user didn't pin a variant.
        // This is the fast path — 0.62 MB ONNX, 35-47 fps Mac WebGPU.
        if (!urlParams.get("student")) {
          const url = new URL(window.location.href);
          url.searchParams.set("student", "q");
          // Push without reload so decoder.create() reads it; URL bar updates for share/debug.
          window.history.replaceState(null, "", url.toString());
        }
        const dec = await window.SemcomVaeDecoder.create();
        if (cancelled || !dec) {
          setDecoderState((d) => ({ ...d, ready: false, error: "WebGPU unavailable" }));
          return;
        }
        if (decodeCanvasRef.current) {
          // v10.48: canvas internal dims = source frame coords from daemon (xc prepped: 1252×1654 portrait).
          // Decoder paints decoded 256×256 face at bbox source-coord position; rest stays transparent.
          // CSS objectFit/objectPosition on this canvas matches LK <video> → face overlays exactly.
          // TODO daemon should send source dims in switch_avatar reply; xc hardcoded for now.
          dec.attachCanvas(decodeCanvasRef.current, 1252, 1654);
          dec.setVideoSourceSize(1252, 1654);
        }
        window.__onSemcomFrame = (f) => { dec.onFrame(f); };
        window.__semcomVaeDecoder = dec;
        setDecoderState((d) => ({ ...d, ready: true, error: null }));
      } catch (e) {
        console.error("[semcom-vae] init err — falling back to LK video", e);
        setDecoderState((d) => ({ ...d, error: e.message || String(e) }));
      }
    })();
    return () => { cancelled = true; };
  }, [labDecode]);

  // 5/18 r5: shop-NNN (non-xiaocheng, non-LiveKit) 的 motion mp4 默认 muted, 加 tap-to-unmute.
  // 复用 marketplace.jsx AvatarPortrait 的 useImperativeHandle.toggleAudio() (经 AudioCtx).
  // 在 InteractStage 内挂自己的 AudioCtx.Provider, activeSlug 跟 shopUnmuted 同步.
  const isShop = !useLiveKit && avatar?.slug && avatar.slug !== "xiaocheng";
  const [shopUnmuted, setShopUnmuted] = useState(false);
  const shopCtxValue = useMemo(() => ({
    activeSlug: shopUnmuted ? avatar?.slug : null,
    setActiveSlug: (s) => setShopUnmuted(!!s),
  }), [shopUnmuted, avatar?.slug]);
  const portraitRef = useRef(null);
  const handleShopUnmute = () => {
    if (portraitRef.current) portraitRef.current.toggleAudio();
  };

  const AudioCtxLocal = window.TvAudioCtx;

  return (
    <div className="interact-stage">
      <div className="stage-video" ref={stageVideoRef}>
        {useLiveKit ? (
          <>
            {/* 5/22 v10.22 真因修: 老 hardcode xiaocheng.png 让 100 shop 进来都先显小橙 6-8s.
               改 dynamic: shop 走自己 portrait, xiaocheng 仍走 assets/xiaocheng.png. */}
            <img
              src={avatar?.slug === "xiaocheng" ? "assets/xiaocheng.png" : (avatar?.portrait || "assets/xiaocheng.png")}
              alt={avatar?.name}
              aria-hidden="true"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                       objectFit: "cover", objectPosition: "50% 18%", zIndex: 0 }}
            />
            <video
              ref={videoRef}
              autoPlay
              muted={!unmuted}
              playsInline
              webkit-playsinline="true"
              poster={avatar?.slug === "xiaocheng" ? "assets/xiaocheng.png" : (avatar?.portrait || "assets/xiaocheng.png")}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                       objectFit: "cover", objectPosition: "50% 18%",
                       background: "transparent", zIndex: 1 }}
            />
            {/* v10.48 — client-decoded face as transparent overlay over LK video face region.
                Canvas internal size = source frame coords (daemon 1252×1654). Decoder paints
                decoded 256×256 face at SemCom v3 bbox position; rest transparent. Same
                objectFit/objectPosition as LK <video> → pixel-aligned face overlay covers LK
                face area exactly. User sees: LK video body + client-decoded face. 0 双脸穿帮. */}
            {labDecode && (
              <canvas
                ref={decodeCanvasRef}
                aria-label="客户端 SemCom 解码"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                         objectFit: "cover", objectPosition: "50% 18%",
                         background: "transparent", zIndex: 2,
                         pointerEvents: "none",
                         visibility: decoderPrimary ? "visible" : "hidden" }}
              />
            )}
          </>
        ) : avatar?.slug === "xiaocheng" ? (
          <img
            src="assets/xiaocheng.png"
            alt={avatar.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 18%" }}
          />
        ) : AudioCtxLocal ? (
          <AudioCtxLocal.Provider value={shopCtxValue}>
            <AvatarPortrait ref={portraitRef} avatar={avatar} />
          </AudioCtxLocal.Provider>
        ) : (
          <AvatarPortrait avatar={avatar} />
        )}
        <div className="stage-vignette"/>

        {/* Live + speaking indicator */}
        <div className="stage-badges">
          <span className="mk-badge mk-badge-live">
            <span className="dot"/>LIVE 互动中
          </span>
          <span className="stage-listening">
            <span className="ear-rings">
              <span/><span/><span/>
            </span>
            <span>{avatar?.name || "TA"} 正在聆听…</span>
          </span>
        </div>

        {/* 5/25 v10.78: lkState 友好状态条 — WS reconnect/preload 时 founder 不再看 console raw 1006 */}
        {useLiveKit && lkState && lkState !== "video_ready" && lkState !== "room_joined" && (
          <div style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            background: "rgba(0,0,0,0.72)", color: "#fff", padding: "12px 20px",
            borderRadius: 12, fontSize: 14, fontWeight: 500, zIndex: 40,
            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.12)", display: "flex", gap: 10, alignItems: "center",
          }}>
            <span style={{
              display: "inline-block", width: 14, height: 14, borderRadius: "50%",
              border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#FF752B",
              animation: "spin 0.9s linear infinite",
            }}/>
            <span>
              {lkState === "idle" || lkState === "lib_loading" ? "加载互动引擎…" :
               lkState === "ws_connecting" ? `连接 ${avatar?.name || "数字人"}…` :
               lkState === "ws_open" ? "切换数字人形象…" :
               lkState === "ws_fail" ? "连接断开, 重连中…" :
               lkState === "room_joining" ? "进入互动房间…" :
               lkState === "room_fail" ? "进入互动房间失败, 重试中…" :
               lkState === "no_lib" ? "加载 LiveKit 引擎失败, 请刷新" : `载入 ${lkState}…`}
            </span>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* v10.5: 删 "点击开启声音" 按钮. 首次用户进 tab 已是 gesture, 直接尝试 unmute. 浏览器拦了浏览器自显原生按钮. */}

        {/* 5/23 Stage B/C — SemCom v3 lab modes */}
        {labMode && (
          <div style={{
            position: "absolute", top: 8, right: 8, zIndex: 50,
            background: "rgba(0,0,0,0.78)", color: "#0f0",
            padding: "8px 12px", borderRadius: 6,
            font: "11px/1.4 ui-monospace, Menlo, monospace",
            border: "1px solid rgba(0,255,0,0.3)",
            backdropFilter: "blur(4px)",
            pointerEvents: "none",
          }}>
            <div style={{ color: "#0ff", marginBottom: 4, fontSize: 10, letterSpacing: 1 }}>
              SEMCOM v3 RX {labDecode ? "+ VAE DECODE" : ""}
            </div>
            <div>frames: {semStats.frames}</div>
            <div>bytes: {(semStats.bytes / 1024).toFixed(1)} KB</div>
            <div>fps: {semStats.fps.toFixed(1)}</div>
            <div>rate: {semStats.kbps.toFixed(0)} kbps</div>
            <div>frame#: {semStats.frameId}</div>
            {semStats.bbox && (
              <div style={{ marginTop: 4, color: "#ff0" }}>
                bbox: ({semStats.bbox[0]},{semStats.bbox[1]}) → ({semStats.bbox[2]},{semStats.bbox[3]})
              </div>
            )}
            {labDecode && (
              <div style={{ marginTop: 6, paddingTop: 4, borderTop: "1px solid rgba(0,255,255,0.2)" }}>
                <div style={{ color: "#0ff", fontSize: 10, letterSpacing: 1, marginBottom: 2 }}>WEBGPU VAE</div>
                <div>ready: {decoderState.ready ? "✓" : (decoderState.error ? "✗" : "loading…")}</div>
                <div>decoded: {decoderState.decoded}</div>
                <div>avg: {decoderState.avgMs.toFixed(1)} ms</div>
                {decoderState.tRun != null && decoderState.decoded > 0 && (
                  <div style={{ marginTop: 2, fontSize: 9, color: "#ccc" }}>
                    copy: {decoderState.tCopy.toFixed(1)} ms<br/>
                    run: {decoderState.tRun.toFixed(1)} ms<br/>
                    post: {decoderState.tPost.toFixed(1)} ms<br/>
                    draw: {decoderState.tDraw.toFixed(1)} ms
                  </div>
                )}
                {decoderState.error && (
                  <div style={{ color: "#f55", marginTop: 2 }}>err: {decoderState.error}</div>
                )}
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: 9, color: "#888" }}>
              {semStats.frames === 0 ? "waiting for face latent…" : "8KB latent/frame stream"}
            </div>
          </div>
        )}

        {/* v10.45 — client-decode face is now PRIMARY (inline above as full-stage canvas).
            Dev-only inline preview removed; live decode replaces LK video directly. */}

        {/* 5/28 v10.84 — stage-subtitle overlay: 当前说的句子镜像到 video 底部 */}
        {useLiveKit && currentSentence && (
          <div className="stage-subtitle">
            <span>{currentSentence}</span>
          </div>
        )}

        {/* 5/28 v10.84 — stage-controls bar: mic / volume / fullscreen / end call */}
        {useLiveKit && (
          <div className="stage-controls" role="toolbar" aria-label="视频通话控件">
            <button
              type="button"
              className="stage-ctl-btn"
              onClick={onSwitchToVoiceTab}
              title="切到语音 (按住说话)"
              aria-label="切到语音"
            >🎤</button>
            <button
              type="button"
              className="stage-ctl-btn"
              onClick={toggleAudioMute}
              title={audioMuted ? "取消静音" : "静音"}
              aria-label={audioMuted ? "取消静音" : "静音"}
            >{audioMuted ? "🔇" : "🔊"}</button>
            <div className="stage-ctl-status">{lkState === "video_ready" || lkState === "room_joined" ? "LIVE" : "连接中…"}</div>
            <button
              type="button"
              className="stage-ctl-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? "退出全屏" : "全屏"}
              aria-label={isFullscreen ? "退出全屏" : "全屏"}
            >⛶</button>
            <button
              type="button"
              className="stage-ctl-btn stage-ctl-hangup"
              onClick={onEndCall}
              title="结束通话, 返回文字模式"
              aria-label="结束通话"
            >❎</button>
          </div>
        )}

      </div>
    </div>
  );
}

/* ============================================================
   弹幕 card
   ============================================================ */
function DanmuCard({ item }) {
  const showQ = item.q != null;
  const showA = item.a != null;
  return (
    <div className={`danmu ${item.mine ? "mine" : ""} ${item.streaming ? "streaming" : ""}`}>
      <div className="danmu-head">
        <div className="danmu-avatar">{item.avatar}</div>
        <div className="danmu-user">{item.user}</div>
        <div className="danmu-time">{item.t}</div>
        {item.firstTokenMs != null && !item.mine && (
          <div className="danmu-ttft" title="首字延迟">
            <span className="dot"/>{item.firstTokenMs}ms
          </div>
        )}
      </div>
      {showQ && <div className="danmu-q">{item.q}</div>}
      {showA && item.a && (
        <div className="danmu-a">
          {!item.mine && <span className="danmu-a-tag">{item.offline ? "离线" : "TA 回答"}</span>}
          <span>{item.a}{item.streaming && <span className="danmu-caret">▍</span>}</span>
        </div>
      )}
      {showA && !item.a && (
        /* v10.5: 删 "TA 正在组织答案" placeholder. 直接显空 + caret 闪烁, 真文字到达填入. */
        <div className="danmu-a danmu-pending">
          <span className="danmu-a-tag">{item.offline ? "离线" : "TA 回答"}</span>
          <span className="danmu-caret">▍</span>
        </div>
      )}
      {item.error && !item.streaming && (
        <div className="danmu-err" title={item.error}>连接断开,自动重连中…</div>
      )}
    </div>
  );
}

/* ============================================================
   Video call permission gate
   ============================================================ */
function VideoPermDialog({ avatar, onCancel, onAccept }) {
  return (
    <div className="vc-backdrop" onClick={onCancel}>
      <div className="vc-perm" onClick={e => e.stopPropagation()}>
        <div className="vc-perm-icon"><i data-lucide="video"></i></div>
        <h3>{avatar?.name || "TA"} 想跟你视频通话</h3>
        <p style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>开启摄像头与麦克风, 仅通话期间用, 不录制</p>
        <div className="vc-perm-actions">
          <button className="vc-perm-cancel" onClick={onCancel}>暂不</button>
          <button className="vc-perm-accept" onClick={onAccept}>
            <i data-lucide="video"></i>
            允许并开始
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Full-screen FaceTime-style video call
   ============================================================ */
function VideoCallView({ avatar, onHangup, lkAttachVideo, lkDetachVideo, lkVideoReady, lkSendAsk, lkInterrupt, lkStartAudio,
                          sttEndpoint, sttBackendLabel, personaText, onDanmu, updateDanmu }) {
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [permErr, setPermErr] = useState(null);
  const [unmutedVc, setUnmutedVc] = useState(false);  // 显式 audio unlock flag
  const pipVideoRef = useRef(null);
  const anchorVideoRef = useRef(null);  // 5/18 modal center: LiveKit lipsync_daemon 嘴动 video
  const userStreamRef = useRef(null);
  /* 5/19 v10.0 视频模态全量重构: 持续视音频 + 百炼 vision 推理.
   * 老 PTT 路径保留 fallback (静默时强制说) 但默认走持续 listen. */
  const [pttRecording, setPttRecording] = useState(false);
  const [pttHint, setPttHint] = useState("按住说话 (备用)");
  const pttRecRef = useRef(null);
  const pttChunksRef = useRef([]);
  const pttMountedAtRef = useRef(Date.now());
  /* === v10.0 continuous listen + vision === */
  const [continuousMode, setContinuousMode] = useState(true);   // 默认开
  const [convoStatus, setConvoStatus] = useState("init");        // init | listening | thinking | speaking | paused
  const [lastVisionText, setLastVisionText] = useState("");
  const contRecRef = useRef(null);     // continuous MediaRecorder
  const contInflightRef = useRef(false);
  const visionInflightRef = useRef(false);
  const lastAskAtRef = useRef(0);      // dedup quick double-asks
  const canvasRef = useRef(null);       // offscreen for frame capture
  /* === v10.1 Phase B-3 AudioContext VAD === */
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  /* v10.10: 防 echo 3 层 + peak VAD + 砍头砍尾修 */
  const chunkPeakRef = useRef(0);          // 跟踪 chunk 内 max RMS (替 instant lv, 修砍头砍尾)
  const lastBotTextRef = useRef("");        // 小橙最新一句, STT 相似 → skip echo
  const postSpeakUntilRef = useRef(0);      // ms 时戳, 之前 skip mic (尾音 grace)
  const vadRafRef = useRef(0);
  const [audioLevel, setAudioLevel] = useState(0);  // RMS 0-1 实时
  const audioLevelRef = useRef(0);   // 同步 ref 给 onstop 判断

  // 真申请用户摄像头 + 麦克风 (user gesture context: openVideoCall click)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setPermErr("浏览器不支持摄像头 API"); return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        userStreamRef.current = stream;
        if (pipVideoRef.current) {
          pipVideoRef.current.srcObject = stream;
          pipVideoRef.current.play().catch(() => {});
        }
      } catch (err) {
        if (!cancelled) setPermErr("摄像头/麦克风权限被拒. 浏览器地址栏左边图标 → 允许 → 重试.");
      }
    })();
    return () => {
      cancelled = true;
      if (userStreamRef.current) {
        userStreamRef.current.getTracks().forEach(t => t.stop());
        userStreamRef.current = null;
      }
    };
  }, []);

  // 5/18 attach LiveKit 数字人嘴动 video 到 modal 中心 <video> element
  useEffect(() => {
    const el = anchorVideoRef.current;
    if (!el || typeof lkAttachVideo !== "function") return;
    lkAttachVideo(el);
    // 解 iOS autoplay 锁 (用 user gesture context)
    if (typeof lkStartAudio === "function") {
      try { lkStartAudio(); } catch {}
    }
    // v10.57: same brute-force audio unlock as InteractRoom (VideoCallView modal
    // catches events before bubbling to document — need its own listener).
    document.querySelectorAll("audio").forEach((a) => {
      try { a.muted = false; a.play().catch(() => {}); } catch {}
    });
    return () => {
      if (typeof lkDetachVideo === "function") {
        try { lkDetachVideo(el); } catch {}
      }
    };
  }, [lkAttachVideo, lkDetachVideo, lkStartAudio]);

  // v10.57: tap-anywhere audio fallback inside video call modal too.
  useEffect(() => {
    if (unmutedVc) return;
    const onGesture = () => {
      try { lkStartAudio?.(); } catch {}
      document.querySelectorAll("audio").forEach((a) => {
        try { a.muted = false; a.play().catch(() => {}); } catch {}
      });
      const anyPlaying = Array.from(document.querySelectorAll("audio")).some((a) => !a.paused && a.readyState > 2);
      if (anyPlaying) setUnmutedVc(true);
    };
    document.addEventListener("click", onGesture, { capture: true });
    document.addEventListener("touchstart", onGesture, { capture: true, passive: true });
    return () => {
      document.removeEventListener("click", onGesture, { capture: true });
      document.removeEventListener("touchstart", onGesture, { capture: true });
    };
  }, [unmutedVc, lkStartAudio]);

  /* ── 5/18 v9.5 bug-B 修: PTT robust — userStream audio 没 track 时 fallback 自己 getUserMedia ── */
  const pttDown = async (e) => {
    e?.preventDefault?.();
    // 5/18 v9.6: 守卫 VideoCallView modal 刚挂载时 iOS touch leak 假触发
    const sinceMount = Date.now() - pttMountedAtRef.current;
    if (sinceMount < 600) {
      console.warn("[ptt] ignore stale pointerdown", sinceMount, "ms after modal mount");
      return;
    }
    if (pttRecording) return;
    setPttRecording(true);
    setPttHint("准备录音…");
    pttChunksRef.current = [];
    try {
      // 优先复用 userStreamRef 的 audio track. 没有 → 再申请独立 audio:true 流 (mic-only fallback)
      let audioOnly = null;
      const s = userStreamRef.current;
      if (s) {
        const audioTracks = s.getAudioTracks();
        if (audioTracks && audioTracks.length > 0 && audioTracks[0].readyState === "live") {
          audioOnly = new MediaStream(audioTracks);
          console.log("[ptt] using reused userStream audio track");
        }
      }
      if (!audioOnly) {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("浏览器不支持 mic API");
        console.log("[ptt] requesting fresh mic stream");
        audioOnly = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      }
      const mime = MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                 : MediaRecorder.isTypeSupported?.("audio/mp4") ? "audio/mp4" : "audio/webm";
      console.log("[ptt] MediaRecorder mime=", mime);
      const rec = new MediaRecorder(audioOnly, { mimeType: mime });
      pttRecRef.current = rec;
      rec.ondataavailable = (ev) => { if (ev.data?.size > 0) pttChunksRef.current.push(ev.data); };
      rec.start();
      setPttHint("🔴 松开发送");
    } catch (err) {
      console.warn("[ptt] start fail", err);
      setPttRecording(false);
      setPttHint("按住说话");
      onDanmu?.({ id: `ptt-perm-${Date.now()}`, mine: false, user: "系统", avatar: "!", t: "录音失败",
                  q: null, a: "麦克风未就绪: " + (err.message || err.name) + " · 请检查浏览器麦克风权限" });
    }
  };
  const pttUp = async () => {
    if (!pttRecording) return;
    setPttRecording(false);
    setPttHint("按住说话");
    const rec = pttRecRef.current;
    pttRecRef.current = null;
    if (!rec || rec.state === "inactive") return;
    await new Promise(res => { rec.onstop = res; rec.stop(); });
    const chunks = pttChunksRef.current;
    pttChunksRef.current = [];
    console.log("[ptt] stopped, chunks=", chunks.length, "size=", chunks.reduce((a,c)=>a+c.size,0));
    if (!chunks.length) return;
    const blob = new Blob(chunks, { type: chunks[0].type || "audio/webm" });
    if (blob.size < 800) {
      console.warn("[ptt] blob too small", blob.size);
      onDanmu?.({ id: `ptt-short-${Date.now()}`, mine: false, user: "系统", avatar: "!", t: "录音太短",
                  q: null, a: "请按住麦克风 ≥1 秒再松开" });
      return;
    }
    const cardId = `vc-ptt-${Date.now()}`;
    onDanmu?.({ id: cardId, mine: true, user: "我", avatar: "我", t: "识别中…",
                 q: "🎤 (" + (blob.size/1024).toFixed(1) + "KB)", a: null, sttPending: true });
    try {
      const form = new FormData();
      form.append("audio", blob, "ptt." + (blob.type.includes("webm") ? "webm" : "m4a"));
      const r = await fetch(sttEndpoint || "https://radio.wanwushop.cn/stt/transcribe",
                            { method: "POST", body: form, credentials: "omit" });
      if (!r.ok) throw new Error("STT " + r.status);
      const j = await r.json();
      const text = (j.text || j.data?.text || "").trim();
      if (!text) { updateDanmu?.(cardId, { q: "🎤 (识别空)", a: "听不清, 再说一遍?" }); return; }
      updateDanmu?.(cardId, { q: text, t: "已发送", sttPending: false });
      console.log("[ptt] STT got text:", text, "→ lkSendAsk");
      // 真送到 LiveKit daemon → TTS + lipsync 推回
      if (typeof lkSendAsk === "function") {
        const replyId = `vc-reply-${Date.now()}`;
        let replyText = "";
        onDanmu?.({ id: replyId, mine: false, user: avatar?.name || "TA", avatar: "🍊", t: "回复中…",
                     q: null, a: "", streaming: true });
        const askId = lkSendAsk(text, personaText || "", {
          onSentence: (s) => { replyText += s; updateDanmu?.(replyId, { a: replyText, streaming: true }); console.log("[ptt] sentence:", s); },
          onDone:     ()  => { updateDanmu?.(replyId, { a: replyText || "(说完了)", streaming: false }); console.log("[ptt] done"); },
          onError:    (m) => { updateDanmu?.(replyId, { a: "出错: " + m, streaming: false }); console.warn("[ptt] error", m); },
        });
        if (!askId) {
          console.warn("[ptt] lkSendAsk returned null — WS not open?");
          updateDanmu?.(replyId, { a: "WebSocket 未连接, 请重新开启视频通话", streaming: false });
        }
      } else {
        console.warn("[ptt] lkSendAsk not a function");
      }
    } catch (err) {
      console.warn("[ptt] STT/ask exception", err);
      updateDanmu?.(cardId, { q: "🎤 STT 异常", a: (sttBackendLabel || "STT") + " 没响应: " + (err.message || err.name) });
    }
  };

  // mute/cam toggle 真控本机流
  useEffect(() => {
    const s = userStreamRef.current; if (!s) return;
    s.getAudioTracks().forEach(t => t.enabled = !muted);
  }, [muted]);
  useEffect(() => {
    const s = userStreamRef.current; if (!s) return;
    s.getVideoTracks().forEach(t => t.enabled = !camOff);
  }, [camOff]);

  /* === v10.1 Phase B-3: AudioContext VAD — 实时 RMS energy 监听 ===
   * 不替 MediaRecorder, 而是 PARALLEL 跑 AnalyserNode 拿实时音量.
   * audioLevelRef.current 提供给 processContChunk 判断: chunk RMS 平均 > 阈值 = 有真人声.
   * 替老"chunk.size > 1200B" 简单判 (b 噪音也能填大 chunk 误触). */
  useEffect(() => {
    if (!continuousMode || muted) return;
    let cancelled = false;
    let interval = null;
    (async () => {
      try {
        // 等 stream 就绪
        for (let i = 0; i < 30; i++) {
          if (userStreamRef.current && userStreamRef.current.getAudioTracks().length > 0) break;
          await new Promise(r => setTimeout(r, 200));
        }
        if (cancelled || !userStreamRef.current) return;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(new MediaStream(userStreamRef.current.getAudioTracks()));
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        source.connect(analyser);
        analyserRef.current = analyser;
        const buf = new Float32Array(analyser.fftSize);
        const tick = () => {
          if (cancelled) return;
          analyser.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
          const rms = Math.sqrt(sum / buf.length);
          audioLevelRef.current = rms;
          /* v10.10: 跟踪 chunk 内峰值 — 修首末 chunk lv 瞬时低被砍头/尾 */
          if (rms > chunkPeakRef.current) chunkPeakRef.current = rms;
          setAudioLevel(rms);
          vadRafRef.current = requestAnimationFrame(tick);
        };
        tick();
        console.log("[vad] AudioContext started, sampleRate=", ctx.sampleRate);
      } catch (e) {
        console.warn("[vad] init fail", e);
      }
    })();
    return () => {
      cancelled = true;
      try { if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current); } catch {}
      try { audioCtxRef.current?.close(); } catch {}
      audioCtxRef.current = null;
      analyserRef.current = null;
    };
  }, [continuousMode, muted]);

  /* v10.58: 极致 voice barge-in — 真实时 RAF VAD, AI speaking 时如果用户峰值 > 0.18
     连续 200ms → 立即 lkInterrupt() + 本地 audio mute. 总延迟 ~250ms (真值).
     高门槛 0.18 抗 AI 余音 echo (普通 VAD 0.05 会被 AI 自己说话触发 → 自打断).
     真用户大声/正常音量约 0.20-0.40 峰值, 真打断 ✓. 真轻声不会触发 ✗ (避免误打断). */
  useEffect(() => {
    if (!continuousMode || muted) return;
    if (convoStatus !== "speaking") return;  // 只在 AI 说话期间侦听
    if (typeof lkInterrupt !== "function") return;

    const BARGE_THRESHOLD = 0.18;
    const REQUIRED_DURATION_MS = 200;
    let aboveSince = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const lv = audioLevelRef.current || 0;
      if (lv >= BARGE_THRESHOLD) {
        if (aboveSince === 0) aboveSince = Date.now();
        else if (Date.now() - aboveSince >= REQUIRED_DURATION_MS) {
          // 真打断 — fire 一次后停 RAF
          console.log(`[barge-in] interrupt @ lv=${lv.toFixed(3)} sustained ${Date.now() - aboveSince}ms`);
          try { lkInterrupt(); } catch {}
          // 本地 audio 立即静音 (防 AI 残余 audio packet 还播)
          document.querySelectorAll("audio").forEach((a) => {
            try { a.muted = true; setTimeout(() => { a.muted = false; }, 600); } catch {}
          });
          onDanmu?.({ id: `barge-${Date.now()}`, mine: false, user: "系统", avatar: "✋", t: "刚刚",
                      q: null, a: "(您插话, 已打断)" });
          cancelled = true;
          return;
        }
      } else {
        aboveSince = 0;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    return () => { cancelled = true; };
  }, [continuousMode, muted, convoStatus, lkInterrupt, onDanmu]);

  /* ============================================================
   * 5/19 v10.0: 持续 listen + 摄像头 frame snapshot + 百炼 vision 注入
   * 流程:
   *   1) userStream 就绪 → 启 continuous MediaRecorder (timeslice 2.5s)
   *   2) 每 2.5s chunk → POST /stt/transcribe → 拿真识别 text
   *   3) text 非空且 >= 2 字 → 抓 PiP video 当前帧 → POST /vision/analyze 拿场景描述
   *   4) lkSendAsk(`[当前画面: ${vision}] ${text}`, persona, callbacks)
   *   5) 服务器 sentence_start 流回 → 用户讲新话 chunk 又拿到 text → 自动中断 (服务器侧 daemon 短路)
   * 状态: init → listening → thinking → speaking → listening
   * mute 时停 listen, unmute 时重启.
   * ============================================================ */
  useEffect(() => {
    if (!continuousMode) { setConvoStatus("paused"); return; }
    if (muted) { setConvoStatus("paused"); return; }
    let cancelled = false;
    let rec = null;
    let restartTimer = null;
    const start = () => {
      if (cancelled) return;
      const s = userStreamRef.current;
      if (!s) {
        restartTimer = setTimeout(start, 500);
        return;
      }
      const audioTracks = s.getAudioTracks();
      if (!audioTracks.length || audioTracks[0].readyState !== "live") {
        restartTimer = setTimeout(start, 500);
        return;
      }
      try {
        const audioOnly = new MediaStream(audioTracks);
        const mime = MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                   : MediaRecorder.isTypeSupported?.("audio/mp4") ? "audio/mp4" : "audio/webm";
        rec = new MediaRecorder(audioOnly, { mimeType: mime });
        contRecRef.current = rec;
        rec.ondataavailable = async (ev) => {
          if (cancelled) return;
          const sz = ev.data?.size || 0;
          /* v10.10: 用 chunk 内峰值 (chunkPeakRef) 不是瞬时, 修首末砍丢. 读完即 reset 给下个 chunk. */
          const peak = chunkPeakRef.current;
          const lv = audioLevelRef.current || 0;
          chunkPeakRef.current = 0;
          const tag = `sz=${sz} peak=${peak.toFixed(4)} lv=${lv.toFixed(4)}`;
          if (sz < 600) { console.log("[cv] skip tiny", tag); return; }
          if (contInflightRef.current) { console.log("[cv] skip inflight", tag); return; }
          /* v10.12: peak 0.005→0.05 — 实测 founder 静默背景噪 peak=0.05-0.07, 真说话 peak=0.1+.
             0.005 太低让真静音 chunk 也送 Whisper → 幻觉"好的,谢谢"/"thank you" 等训练数据常见短语. */
          if (peak < 0.05) { console.log("[cv] skip vad peak (silence)", tag); return; }
          /* v10.10 echo 防 3 层 — A: convoStatus thinking/speaking 都 skip (覆盖 ask→sentence_start gap) */
          if (convoStatus === "speaking" || convoStatus === "thinking") {
            console.log("[cv] skip during bot turn", convoStatus, tag); return;
          }
          /* v10.10 echo 防 3 层 — B: 小橙说完 1.5s 尾音 grace 内全 skip */
          if (Date.now() < postSpeakUntilRef.current) {
            console.log("[cv] skip post-speak grace", tag); return;
          }
          console.log("[cv] PROCESS chunk", tag);
          contInflightRef.current = true;
          try {
            await processContChunk(ev.data);
          } finally {
            contInflightRef.current = false;
          }
        };
        rec.onstop = () => {
          if (!cancelled) {
            // re-start next slice (Safari MR 不支持 long timeslice, 我们手动重起)
            restartTimer = setTimeout(start, 80);
          }
        };
        rec.start();
        setConvoStatus("listening");
        console.log("[cv] continuous rec started", mime);
        /* v10.10: 2500 → 3500ms timeslice. 给 Whisper 多 1s context, 减"thank you" 类幻觉 + 砍头砍尾 */
        restartTimer = setTimeout(() => {
          try { if (rec && rec.state === "recording") rec.stop(); } catch {}
        }, 3500);
      } catch (err) {
        console.warn("[cv] start fail", err);
        restartTimer = setTimeout(start, 1000);
      }
    };
    start();
    return () => {
      cancelled = true;
      if (restartTimer) { try { clearTimeout(restartTimer); } catch {} }
      try { rec && rec.state !== "inactive" && rec.stop(); } catch {}
      contRecRef.current = null;
    };
  }, [continuousMode, muted]);

  // === 处理一个 2.5s 音频 chunk ===
  const processContChunk = async (blob) => {
    const sttUrl = "https://radio.wanwushop.cn/stt/transcribe";
    const visionUrl = "https://radio.wanwushop.cn/vision/analyze";
    let text = "";
    try {
      const form = new FormData();
      form.append("audio", blob, "cv." + (blob.type.includes("webm") ? "webm" : "m4a"));
      /* v10.12: 多语言 — 默认不指定 lang, 让 Whisper auto-detect. 用户说英文/日文也能识别.
         小橙的 persona 后续 (server-side) 加 "用户什么语言你回什么语言" 规则.
         留尾: 中文识别精度可能略降 (Whisper auto 偶发中→英误判), 但换来跨语言支持.
         如真中文场景多, 可加 UI 切换 lang query param. */
      const r = await fetch(sttUrl, { method: "POST", body: form, credentials: "omit" });
      if (!r.ok) { console.warn("[cv] STT", r.status); return; }
      const j = await r.json();
      text = (j.text || j.data?.text || "").trim();
    } catch (err) {
      console.warn("[cv] STT err", err.message);
      return;
    }
    console.log("[cv] STT raw:", JSON.stringify(text));
    /* v10.4 大幅加强噪声过滤 — founder 实测 "oh." 带标点滑过黑名单导致小橙一直答 garbage.
       1. strip 所有 punct/whitespace/emoji 再判
       2. 单字 + 短词 + 重复字符 (嗯嗯嗯 / mmm / .....) 全跳
       3. xiaocheng speaking 时门槛更严 (≥4 字) */
    const normalized = text.replace(/[\s\p{P}\p{S}]/gu, '').toLowerCase();
    if (!normalized || normalized.length < 2) { console.log("[cv] skip empty/short", JSON.stringify(normalized)); return; }
    /* 重复字符: 嗯嗯嗯 / mmm / hahaha / yes yes yes 全跳 */
    if (/^(.)\1+$/.test(normalized)) { console.log("[cv] skip repeat-char", normalized); return; }
    /* v10.12: 加 Whisper 幻觉常见短语黑名单 — 训练数据 YouTube 字幕大量"谢谢观看"/"好的谢谢"/"thank you for watching" 等. */
    const whisperHalluc = /^(好的谢谢|好的|谢谢|谢谢观看|谢谢大家|多谢|thanks for watching|thank you for watching|thanks|thankyou|thank you|please subscribe|like and subscribe|请订阅|see you next time|下次再见|byebye|byeee+)$/i;
    if (whisperHalluc.test(normalized)) { console.log("[cv] skip whisper halluc:", normalized); return; }
    /* 全英文噪声词: oh ah um hi hey yeah ok mm hm hmm 等 + 全中文噪声 嗯哦啊呃喔咦呀哎 */
    const blacklist = /^(嗯+|哦+|啊+|呃+|喔+|咦+|呀+|哎+|hello|hi+|hey+|yeah+|yep|nope|nah|um+|uh+|ah+|oh+|mm+|hm+|hmm+|ok+|okay|ya+|yo+|wow+|hey there|i see|i know|right|sure|good|bad|yes|no|maybe)$/i;
    if (blacklist.test(normalized)) { console.log("[cv] skip noise word:", normalized); return; }
    /* xiaocheng 说话时门槛更严: 必须 ≥4 字才算真打断, 不然误触一直被打断 */
    /* v10.8+: 小橙说话时, mic 全屏蔽 STT (扬声器场景防 echo loop — 浏览器 AEC 减不掉的余音都拦).
       原 length<4 阈值不够, founder 实测 "你好, 我是小程。" 整句被识进 chunk → ask 回环. */
    if (convoStatus === "speaking") {
      console.log("[cv] skip-while-speaking:", normalized);
      return;
    }
    /* dedup: 1.5s 内同样的文本不重复送 (v10.2 下调让连续讲话更顺畅) */
    if (Date.now() - lastAskAtRef.current < 1500) { console.log("[cv] skip dedup"); return; }
    lastAskAtRef.current = Date.now();
    console.log("[cv] STT:", text);

    /* 🎯 Phase B-1 真中断: 小橙正在说话时用户插话 → 先 interrupt 砍 in-flight task 再发新 ask. */
    if (convoStatus === "speaking" && typeof lkInterrupt === "function") {
      console.log("[cv] INTERRUPT — user spoke mid-reply");
      lkInterrupt();
      onDanmu?.({ id: `int-${Date.now()}`, mine: false, user: "系统", avatar: "✋", t: "刚刚",
                  q: null, a: "(中断了小橙的回答)" });
    }
    setConvoStatus("thinking");

    /* 抓帧 + vision 推理 (并行 fire) */
    let visionText = "";
    if (!camOff && !visionInflightRef.current && pipVideoRef.current) {
      try {
        visionInflightRef.current = true;
        const v = pipVideoRef.current;
        const w = v.videoWidth || 320, h = v.videoHeight || 240;
        if (w > 0 && h > 0) {
          let canvas = canvasRef.current;
          if (!canvas) {
            canvas = document.createElement("canvas");
            canvasRef.current = canvas;
          }
          canvas.width = Math.min(640, w);
          canvas.height = Math.min(480, h * canvas.width / w);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          const visionBlob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.7));
          if (visionBlob) {
            const vform = new FormData();
            vform.append("image", visionBlob, "snap.jpg");
            const vr = await fetch(visionUrl, { method: "POST", body: vform, credentials: "omit" });
            if (vr.ok) {
              const vj = await vr.json();
              visionText = (vj.text || "").trim();
              if (visionText) {
                setLastVisionText(visionText);
                console.log("[cv] vision:", visionText);
              }
            }
          }
        }
      } catch (err) {
        console.warn("[cv] vision err", err.message);
      } finally {
        visionInflightRef.current = false;
      }
    }

    /* v10.13: 语言自适应 wrapping. v10.12 全中文 wrap 压倒英文内容 → LLM 仍中文回.
       检测用户文本主体语言: 字母占比 > 50% → 英文 wrap, 否则中文 wrap. + 末尾显式 lang hint 加强. */
    const cjkChars = (text.match(/[一-鿿぀-ヿ가-힯]/g) || []).length;
    const totalNonSpace = text.replace(/\s+/g, '').length || 1;
    const isCJK = cjkChars / totalNonSpace > 0.3;
    const askText = visionText
      ? (isCJK
          ? `(你正在跟客户视频通话, 你能通过摄像头看到客户: ${visionText}) 客户对你说: ${text} [请用客户说话的同种语言回复]`
          : `(You are video-calling a customer. Via the camera you can see them: ${visionText}) Customer says: ${text} [Reply in the SAME language as the customer.]`)
      : (isCJK
          ? `${text} [请用客户说话的同种语言回复]`
          : `${text} [Reply in the SAME language as the customer.]`);
    console.log("[cv] ask:", askText.slice(0, 100));
    if (typeof lkSendAsk === "function") {
      let replyText = "";
      const replyId = `cv-reply-${Date.now()}`;
      onDanmu?.({ id: replyId, mine: true, user: "我", avatar: "我", t: "刚刚", q: text, a: null });
      const askId = lkSendAsk(askText, personaText || "", {
        onSentence: (s) => {
          replyText += s;
          lastBotTextRef.current = replyText;  /* v10.10 echo 防 3 层 — C 数据源 */
          setConvoStatus("speaking");
        },
        onDone:     ()  => {
          updateDanmu?.(replyId, { a: replyText || "(说完了)" });
          /* v10.10 echo 防 3 层 — B: speak 完 1.5s 尾音 grace, mic 仍 skip */
          postSpeakUntilRef.current = Date.now() + 1500;
          setConvoStatus("listening");
        },
        onError:    (m) => {
          updateDanmu?.(replyId, { a: "出错: " + m });
          setConvoStatus("listening");
        },
      });
      if (!askId) {
        console.warn("[cv] lkSendAsk null → WS not open");
        updateDanmu?.(replyId, { a: "连接掉了, 重新进入视频通话" });
        setConvoStatus("listening");
      }
    }
  };

  // duration timer
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const dur = `${String(Math.floor(secs/60)).padStart(2,"0")}:${String(secs%60).padStart(2,"0")}`;

  return (
    <div className="vc-fullscreen">
      {/* 左上 ✕ 关闭 button — fallback close (除了底部红挂断, 多一个路径不被堵) */}
      <button
        onClick={onHangup}
        aria-label="关闭"
        style={{ position: "absolute", top: 16, left: 16, zIndex: 50,
                 width: 40, height: 40, borderRadius: "50%",
                 background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.25)",
                 color: "white", cursor: "pointer", fontSize: 22, fontWeight: 600,
                 display: "flex", alignItems: "center", justifyContent: "center" }}>
        ✕
      </button>

      <div className="vc-anchor">
        {/* 5/22 v10.27: 100 shop + xiaocheng 全走 LK push stream (daemon publisher).
           不再 AvatarPortrait 本地循环 motion mp4 (founder "视频模式应该和文字、语音一样直接推流").
           shop slug 用自己 portrait 当 backdrop, xiaocheng 用 assets/xiaocheng.png. */}
        <div style={{ width: "100%", height: "100%", position: "relative", background: "#000" }}>
          <img src={avatar?.slug === "xiaocheng" ? "assets/xiaocheng.png" : (avatar?.portrait || "assets/xiaocheng.png")} alt=""
               style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "cover", background: "#000",
                        objectPosition: "50% 18%", zIndex: 0 }}/>
          <video ref={anchorVideoRef} playsInline autoPlay
                 style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                          objectFit: "cover", objectPosition: "50% 18%",
                          background: "transparent",
                          opacity: lkVideoReady ? 1 : 0, transition: "opacity 0.4s",
                          zIndex: 1 }}/>
          {/* v10.56 (5/24): 删 "LiveKit 嘴动加载中…" 提示文字 — 暴露技术栈词不该让 user 看. */}
        </div>
        <div className="vc-anchor-overlay"/>

        {/* v10.5: 删 "点击开启声音". 用户已点"允许并开始" = user gesture, 在 onAccept 直接尝试 startAudio. */}
      </div>

      {/* PiP — 真用户摄像头流 (替原"你的画面" placeholder) */}
      <div className="vc-pip">
        {permErr ? (
          <div className="vc-pip-self" style={{ fontSize: 11, padding: 8, textAlign: "center" }}>
            <i data-lucide="alert-triangle"></i>
            <span>{permErr}</span>
          </div>
        ) : !camOff ? (
          <video ref={pipVideoRef} playsInline muted autoPlay
                 style={{ width: "100%", height: "100%", objectFit: "cover",
                          borderRadius: "inherit", background: "#000",
                          transform: "scaleX(-1)" /* 镜像本地预览 */ }}/>
        ) : (
          <div className="vc-pip-off">
            <i data-lucide="video-off"></i>
          </div>
        )}
      </div>

      {/* top status */}
      <div className="vc-status">
        <span className="vc-name">{avatar?.name || "TA"}</span>
        <span className="vc-dur">
          <span className="dot"/>{dur}
        </span>
      </div>

      {/* v10.5: 删绿"听你说"状态条. 听/答状态由麦克风按钮声波动画呈现 (下方 vc-controls). */}
      <style>{`
        @keyframes cv-blink { 0%,100%{opacity:1} 50%{opacity:0.35} }
        @keyframes vc-mic-pulse { 0%,100%{box-shadow: 0 0 0 0 rgba(80,200,120,0.55)} 50%{box-shadow: 0 0 0 10px rgba(80,200,120,0)} }
        .vc-mic-wrap { position: relative; }
        .vc-mic-wrap.listening::after {
          content: ""; position: absolute; inset: 0; border-radius: 50%;
          animation: vc-mic-pulse 1.4s ease-out infinite; pointer-events: none;
        }
        .vc-mic-wrap.speaking::after {
          content: ""; position: absolute; inset: 0; border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(246,145,85,0.4); pointer-events: none;
        }
      `}</style>

      {/* controls — 加 inline pointer-events 保证不被 overlay 挡 */}
      {/* 5/18 v9.7: <i data-lucide={dynamic}> 在 Safari/WebKit 会因 lucide 替 SVG 后 React 找不原 <i> 而炸. 改内联 SVG. */}
      <div className="vc-controls" style={{ pointerEvents: "auto", zIndex: 40 }}>
        {/* v10.5: 麦克风按钮加声波 — convoStatus listening 时显 audioLevel 实时声波条 + 绿色 pulse ring */}
        <div className={`vc-mic-wrap ${!muted && convoStatus === "listening" ? "listening" : ""} ${!muted && convoStatus === "speaking" ? "speaking" : ""}`}>
        <button className={`vc-btn vc-mic-btn ${muted ? "active" : ""}`} onClick={() => setMuted(m => !m)} aria-label="静音">
          {muted ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="2" y1="2" x2="22" y2="22"/><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" y1="19" x2="12" y2="22"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/>
            </svg>
          )}
        </button>
        {/* v10.5: 5 根声波条, scaleY 跟 audioLevel 实时 — 替绿色"听你说"pill */}
        {!muted && convoStatus !== "paused" && (
          <div style={{
            position: "absolute", bottom: -14, left: "50%", transform: "translateX(-50%)",
            display: "flex", alignItems: "flex-end", gap: 2, height: 10, pointerEvents: "none",
          }}>
            {[0, 1, 2, 3, 4].map(i => {
              const phase = (i - 2) * 0.15;
              const base = Math.max(0.2, Math.min(1, audioLevel * 18 + phase));
              return (
                <span key={i} style={{
                  width: 3, height: `${base * 10}px`,
                  background: convoStatus === "speaking" ? "rgba(246,145,85,0.95)" : "rgba(80,220,140,0.95)",
                  borderRadius: 2, transition: "height 70ms ease-out",
                }}/>
              );
            })}
          </div>
        )}
        </div>
        <button className="vc-btn vc-hangup" onClick={onHangup} aria-label="挂断">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="23" y1="1" x2="1" y2="23"/>
          </svg>
        </button>
        <button className={`vc-btn ${camOff ? "active" : ""}`} onClick={() => setCamOff(c => !c)} aria-label="切换摄像头">
          {camOff ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.66 6H14a2 2 0 0 1 2 2v2.34l1 1L23 7v10"/><path d="M21 21H3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3l3 3"/><line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Sales conversion entry cards (right column under timeline)
   ============================================================ */
function SalesEntries({ avatar }) {
  return (
    <div className="sales-entries">
      <a href="#" className="sales-card">
        <div className="sales-card-icon"><i data-lucide="store"></i></div>
        <div className="sales-card-text">
          <div className="sales-card-title">找 TA 服务 / 购买</div>
          <div className="sales-card-sub">跳转实体店 / 商品页</div>
        </div>
        <i data-lucide="arrow-up-right" className="sales-card-arrow"></i>
      </a>
      <a href="#" className="sales-card sales-card-accent">
        <div className="sales-card-icon"><i data-lucide="sparkles"></i></div>
        <div className="sales-card-text">
          <div className="sales-card-title">打造我的同款数字人</div>
          <div className="sales-card-sub">10 分钟 · 上线我的店铺</div>
        </div>
        <i data-lucide="arrow-up-right" className="sales-card-arrow"></i>
      </a>
    </div>
  );
}

Object.assign(window, { InteractRoom, InteractStage, DanmuCard, VideoCallView, SalesEntries });
